import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ShieldPlus, SignOut, ChartPieSlice, Users, ClipboardText, Buildings,
  CurrencyInr, TrendUp, Clock, CheckCircle, Plus, Files, Bank, WhatsappLogo,
  UploadSimple, Trash, PencilSimple, UsersThree, QrCode, Question, ShieldStar, DownloadSimple, EnvelopeSimple, FileText, FileXls, Bell, Phone, PaperPlaneTilt, Check,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail, downloadText, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Logo } from "@/components/insurehub/Logo";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const STATUS = ["Submitted", "Under Review", "Approved", "Rejected", "Settled"];
const statusColors = {
  Submitted: "bg-blue-100 text-blue-800",
  "Under Review": "bg-amber-100 text-amber-800",
  Approved: "bg-emerald-100 text-emerald-800",
  Settled: "bg-emerald-100 text-emerald-800",
  Rejected: "bg-red-100 text-red-800",
};
const barColors = ["#3b82f6", "#f59e0b", "#10b981", "#ef4444", "#065f46"];

const BASE_NAV = [
  { key: "overview", label: "Overview", icon: ChartPieSlice },
  { key: "claims", label: "Claims", icon: ClipboardText },
  { key: "tickets", label: "Service Tickets", icon: Question },
  { key: "callbacks", label: "Callbacks", icon: Phone },
  { key: "bookings", label: "Health Check-ups", icon: FileText },
  { key: "opd", label: "OPD Plans", icon: ShieldStar },
  { key: "payments", label: "Payments", icon: CurrencyInr },
  { key: "employees", label: "Employees", icon: Users },
  { key: "documents", label: "Documents", icon: Files },
  { key: "faqs", label: "FAQ Manager", icon: Question },
  { key: "terms", label: "Policy T&C", icon: FileText },
  { key: "network", label: "Network Hospitals", icon: Buildings },
  { key: "insurers", label: "Insurers & TPA", icon: Bank },
  { key: "whatsapp", label: "WhatsApp", icon: WhatsappLogo },
];

const TONE = {
  emerald: "bg-emerald-100 text-emerald-700",
  blue: "bg-blue-100 text-blue-700",
  amber: "bg-amber-100 text-amber-700",
};

const TICKET_SLA_HOURS = 24;
function ticketAgeInfo(createdAt, status) {
  if (!createdAt) return { text: "—", breached: false };
  const ms = Date.now() - new Date(createdAt).getTime();
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const text = h >= 24 ? `${Math.floor(h / 24)}d ${h % 24}h` : h >= 1 ? `${h}h ${m}m` : `${m}m`;
  const breached = status !== "Resolved" && h >= TICKET_SLA_HOURS;
  return { text, breached, hours: h };
}


function Metric({ icon: Icon, label, value, sub, tone = "emerald" }) {
  const [bg, text] = (TONE[tone] || TONE.emerald).split(" ");
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-6 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
      <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center mb-4`}>
        <Icon size={22} weight="duotone" className={text} />
      </div>
      <p className="text-2xl font-heading font-semibold text-emerald-950">{value}</p>
      <p className="text-sm text-zinc-500">{label}</p>
      {sub && <p className="text-xs text-emerald-600 mt-1">{sub}</p>}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const isMaster = user?.role === "master_admin";
  const NAV = isMaster ? [...BASE_NAV, { key: "admins", label: "Admins", icon: ShieldStar }] : BASE_NAV;
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [claims, setClaims] = useState([]);
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
  const [billPreview, setBillPreview] = useState(null);
  const [welcomingAll, setWelcomingAll] = useState(false);
  const flaggedCount = claims.filter((c) => c.review_flag).length;
  const webhookUrl = typeof window !== "undefined" ? `${window.location.origin}/api/whatsapp/webhook` : "";
  const visibleClaims = showFlaggedOnly ? claims.filter((c) => c.review_flag) : claims;
  const authToken = (typeof window !== "undefined" && localStorage.getItem("insurehub_token")) || "";
  const billUrl = (id) => `${API}/admin/claims/${id}/bill?token=${authToken}`;
  const [employees, setEmployees] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [insurers, setInsurers] = useState([]);
  const [whatsapp, setWhatsapp] = useState({ connected: false, provider: "Twilio", number: "", status: "Not connected" });
  const [addOpen, setAddOpen] = useState(false);
  const [newEmp, setNewEmp] = useState({ name: "", email: "", mobile: "", company: "", employee_code: "", uhid: "", policy_number: "", policy_expiry: "", opd_cover: "", dob: "", plan_name: "Silver Plan", sum_insured: 500000, dependents: [] });
  const [policyTerms, setPolicyTerms] = useState([]);
  const [termForm, setTermForm] = useState({ policy_number: "", terms: "" });
  const loadPolicyTerms = async () => { try { const r = await api.get("/admin/policy-terms"); setPolicyTerms(r.data); } catch (e) {} };
  const saveTerms = async (policy_number, terms) => {
    if (!policy_number.trim()) { toast.error("Enter a policy number."); return; }
    try { await api.post("/admin/policy-terms", { policy_number: policy_number.trim(), terms }); toast.success("Terms saved"); setTermForm({ policy_number: "", terms: "" }); loadPolicyTerms(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const deleteTerms = async (pn) => { try { await api.delete(`/admin/policy-terms/${encodeURIComponent(pn)}`); toast.success("Removed"); loadPolicyTerms(); } catch (e) { toast.error("Couldn't delete"); } };
  const [lookupQuery, setLookupQuery] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [onboardOpen, setOnboardOpen] = useState(false);
  const [onboardPreview, setOnboardPreview] = useState(null);
  const [onboardCh, setOnboardCh] = useState({ email: true, whatsapp: false });
  const [onboardOnlyNew, setOnboardOnlyNew] = useState(true);
  const [onboardBusy, setOnboardBusy] = useState(false);
  const [lastSkipped, setLastSkipped] = useState(null);
  const [companyFilter, setCompanyFilter] = useState("all");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ company: "all", policy_number: "", policy_expiry: "", sum_insured: "", plan_name: "", opd_cover: "" });
  const [bulkSaving, setBulkSaving] = useState(false);
  const applyBulkPolicy = async () => {
    const payload = { company: bulkForm.company };
    if (bulkForm.policy_number.trim()) payload.policy_number = bulkForm.policy_number.trim();
    if (bulkForm.policy_expiry.trim()) payload.policy_expiry = bulkForm.policy_expiry.trim();
    if (String(bulkForm.sum_insured).trim() !== "") payload.sum_insured = Number(bulkForm.sum_insured);
    if (bulkForm.plan_name) payload.plan_name = bulkForm.plan_name;
    if (String(bulkForm.opd_cover).trim() !== "") payload.opd_cover = Number(bulkForm.opd_cover);
    if (!payload.policy_number && !payload.policy_expiry && payload.sum_insured === undefined && !payload.plan_name && payload.opd_cover === undefined) {
      toast.error("Enter at least one of Policy Number, Expiry, Sum Insured, Plan or OPD cover."); return;
    }
    setBulkSaving(true);
    try {
      const { data } = await api.post("/admin/employees/bulk-policy", payload);
      toast.success(`Updated ${data.updated} employee(s)${data.company !== "all" ? ` in ${data.company}` : ""}.`);
      setBulkOpen(false);
      setBulkForm({ company: "all", policy_number: "", policy_expiry: "", sum_insured: "", plan_name: "", opd_cover: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBulkSaving(false); }
  };
  const [bulkUndo, setBulkUndo] = useState({ available: false });
  const [selectedEmps, setSelectedEmps] = useState(new Set());
  const loadUndoStatus = async () => {
    try { const { data } = await api.get("/admin/employees/bulk-policy/undo-status"); setBulkUndo(data); }
    catch (e) { setBulkUndo({ available: false }); }
  };
  const undoBulkPolicy = async () => {
    if (!window.confirm("Undo the most recent bulk policy update? This restores the previous values for those employees.")) return;
    try {
      const { data } = await api.post("/admin/employees/bulk-policy/undo");
      toast.success(`Reverted ${data.restored} employee(s).`);
      setBulkUndo({ available: false });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const toggleSelectEmp = (id) => setSelectedEmps((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = (ids, checked) => setSelectedEmps((s) => { const n = new Set(s); ids.forEach((id) => checked ? n.add(id) : n.delete(id)); return n; });
  const deleteSelectedEmps = async () => {
    const ids = [...selectedEmps];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} selected employee(s)? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/admin/employees/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} employee(s).`);
      setSelectedEmps(new Set());
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const openOnboard = async () => {
    setOnboardOpen(true); setOnboardPreview(null);
    try { const { data } = await api.get("/admin/employees/onboarding-preview"); setOnboardPreview(data); }
    catch (e) { toast.error("Couldn't load preview"); }
  };
  const runOnboard = async () => {
    if (!onboardCh.email && !onboardCh.whatsapp) { toast.error("Pick at least one channel."); return; }
    setOnboardBusy(true);
    try {
      const { data } = await api.post("/admin/employees/onboard", { ...onboardCh, only_new: onboardOnlyNew });
      toast.success(`Onboarding sent — ${data.email_sent} emails, ${data.whatsapp_sent} WhatsApp (of ${data.total})`);
      if (!data.channels_ready.email && onboardCh.email) toast.warning("Email is in DEMO mode — verify your Resend domain to actually deliver.");
      setOnboardOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setOnboardBusy(false); }
  };
  const doLookup = async (e) => {
    if (e) e.preventDefault();
    if (!lookupQuery.trim()) { toast.error("Enter an employee code, phone or member ID."); return; }
    setLookupBusy(true); setLookupResult(null);
    try {
      const { data } = await api.get(`/lookup/member?query=${encodeURIComponent(lookupQuery.trim())}`);
      setLookupResult(data);
    } catch (err) {
      setLookupResult(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No member found");
    } finally { setLookupBusy(false); }
  };
  const [depEmp, setDepEmp] = useState(null); // employee whose dependents dialog is open
  const [newDep, setNewDep] = useState({ name: "", relation: "Spouse", age: "" });
  const [insurerOpen, setInsurerOpen] = useState(false);
  const [newInsurer, setNewInsurer] = useState({ name: "", type: "Insurer", contact: "", website: "" });
  const [faqs, setFaqs] = useState([]);
  const [faqOpen, setFaqOpen] = useState(false);
  const [newFaq, setNewFaq] = useState({ question: "", answer: "", category: "Coverage", product: "Group Health" });
  const [admins, setAdmins] = useState([]);
  const [adminOpen, setAdminOpen] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ name: "", email: "", mobile: "", password: "", role: "admin" });
  const [waLogs, setWaLogs] = useState([]);
  const [digest, setDigest] = useState(null);
  const [digestCfg, setDigestCfg] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [vendorEmail, setVendorEmail] = useState("");
  const [emailingVendor, setEmailingVendor] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [autoEmailVendor, setAutoEmailVendor] = useState(false);
  const [cashlessEmail, setCashlessEmail] = useState("");
  const [reimbEmail, setReimbEmail] = useState("");
  const [supportEmail, setSupportEmail] = useState("");
  const teamSavedRef = React.useRef({ cashless_team_email: "", reimbursement_team_email: "", support_team_email: "" });
  const [shotPreview, setShotPreview] = useState(null);
  const [opdPlans, setOpdPlans] = useState([]);
  const [checkupPackages, setCheckupPackages] = useState([]);
  const [payments, setPayments] = useState([]);
  const [opdEnrollments, setOpdEnrollments] = useState([]);
  const [opdUtil, setOpdUtil] = useState([]);
  const [utilForm, setUtilForm] = useState({ enrollment_id: "", benefit: "", amount: "", note: "" });
  const [callbacks, setCallbacks] = useState([]);
  const [opdFaqs, setOpdFaqs] = useState([]);
  const [faqForm, setFaqForm] = useState({ question: "", answer: "" });
  const loadOpdFaqs = async () => { try { const r = await api.get("/opd/faqs"); setOpdFaqs(r.data); } catch (e) {} };
  const addOpdFaq = async () => {
    if (!faqForm.question.trim() || !faqForm.answer.trim()) { toast.error("Enter a question and answer."); return; }
    try { await api.post("/admin/opd/faqs", { ...faqForm, order: opdFaqs.length }); toast.success("FAQ added"); setFaqForm({ question: "", answer: "" }); loadOpdFaqs(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const deleteOpdFaq = async (id) => { try { await api.delete(`/admin/opd/faqs/${id}`); toast.success("FAQ removed"); loadOpdFaqs(); } catch (e) { toast.error("Couldn't delete"); } };
  const markCallbackDone = async (id) => {
    try { await api.patch(`/admin/callbacks/${id}`); toast.success("Marked as done"); load(); }
    catch (err) { toast.error("Couldn't update"); }
  };

  const load = async () => {
    try {
      const [s, c, e, t, b, cfg, opd, cpk, pay] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/claims"),
        api.get("/admin/employees"),
        api.get("/admin/tickets"),
        api.get("/admin/bookings"),
        api.get("/admin/settings"),
        api.get("/admin/opd/plans"),
        api.get("/admin/checkup/packages"),
        api.get("/admin/payments"),
      ]);
      const [enr, util] = await Promise.all([
        api.get("/admin/opd/enrollments"),
        api.get("/admin/opd/utilization"),
      ]);
      setOpdEnrollments(enr.data);
      setOpdUtil(util.data);
      try { const cb = await api.get("/admin/callbacks"); setCallbacks(cb.data); } catch (e) {}
      setStats(s.data);
      setClaims(c.data);
      setEmployees(e.data);
      setTickets(t.data);
      setBookings(b.data);
      setVendorEmail(cfg.data.vendor_email || "");
      setAutoEmailVendor(!!cfg.data.auto_email_vendor);
      setCashlessEmail(cfg.data.cashless_team_email || "");
      setReimbEmail(cfg.data.reimbursement_team_email || "");
      setSupportEmail(cfg.data.support_team_email || "");
      teamSavedRef.current = {
        cashless_team_email: cfg.data.cashless_team_email || "",
        reimbursement_team_email: cfg.data.reimbursement_team_email || "",
        support_team_email: cfg.data.support_team_email || "",
      };
      setOpdPlans(opd.data);
      setCheckupPackages(cpk.data);
      setPayments(pay.data);
      loadUndoStatus();
    } catch (err) {
      toast.error("Failed to load dashboard data");
    }
  };

  const updateOpdPlan = async (id, patch) => {
    try {
      await api.patch(`/admin/opd/plans/${id}`, patch);
      toast.success("OPD plan updated");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const updateCheckupPkg = async (id, patch) => {
    try {
      await api.patch(`/admin/checkup/packages/${id}`, patch);
      toast.success("Package updated");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const isTrackableLimit = (limit) => /^₹\s*[\d,]+$/.test((limit || "").trim()) || /^\d+\s*session/i.test((limit || "").trim());
  const addUtil = async () => {
    if (!utilForm.enrollment_id || !utilForm.benefit || !utilForm.amount) { toast.error("Select an employee, a benefit and an amount."); return; }
    try {
      await api.post("/admin/opd/utilization", { ...utilForm, amount: Number(utilForm.amount) });
      toast.success("OPD usage logged");
      setUtilForm({ enrollment_id: "", benefit: "", amount: "", note: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const deleteUtil = async (id) => {
    try {
      await api.delete(`/admin/opd/utilization/${id}`);
      toast.success("Usage entry removed");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const saveVendorSettings = async (patch) => {
    try {
      await api.patch("/admin/settings", patch);
    } catch (err) { toast.error("Couldn't save vendor settings"); }
  };
  const saveTeamSettings = async (patch) => {
    const [k, v] = Object.entries(patch)[0];
    if (teamSavedRef.current[k] === v) return;
    try {
      await api.patch("/admin/settings", patch);
      teamSavedRef.current[k] = v;
      toast.success("Routing settings saved");
    } catch (err) { toast.error("Couldn't save routing settings"); }
  };
  const toggleAutoEmail = async () => {
    const next = !autoEmailVendor;
    setAutoEmailVendor(next);
    if (next && vendorEmail) await saveVendorSettings({ vendor_email: vendorEmail, auto_email_vendor: true });
    else await saveVendorSettings({ auto_email_vendor: next });
    toast.success(next ? "Auto-email to Lupin ON — new bookings will be emailed automatically" : "Auto-email to Lupin OFF");
  };

  const updateTicket = async (id, patch) => {
    try {
      await api.patch(`/admin/tickets/${id}`, patch);
      toast.success("Ticket updated");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const updateBooking = async (id, patch) => {
    try {
      await api.patch(`/admin/bookings/${id}`, patch);
      toast.success("Booking updated");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const exportXlsx = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error("Export failed"); }
  };
  const emailVendor = async () => {
    if (!vendorEmail) { toast.error("Enter the vendor's email (Lupin)."); return; }
    setEmailingVendor(true);
    try {
      const { data } = await api.post("/admin/bookings/email-vendor", { email: vendorEmail });
      if (!data.email_live) toast.info("Email not live yet — add your Resend key first.");
      else if (data.ok) toast.success(`Sent ${data.count} booking(s) to ${data.sent_to}`);
      else toast.error("Couldn't email vendor — check Resend domain verification.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setEmailingVendor(false); }
  };
  const shotUrl = (id) => `${API}/bookings/${id}/screenshot?token=${authToken}`;
  const downloadReport = async (id, filename) => {
    try {
      const res = await api.get(`/bookings/${id}/report`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename || "report.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error("Report not available"); }
  };
  const uploadReport = async (id, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/bookings/${id}/report`, fd);
      toast.success("Report uploaded — patient notified");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const sendReminders = async () => {
    setSendingReminders(true);
    try {
      const { data } = await api.get("/admin/bookings/reminders?send=true");
      if (data.reminders_sent > 0) toast.success(`Sent ${data.reminders_sent} reminder(s) for ${data.target_date}`);
      else toast.info(`No check-ups scheduled for tomorrow (${data.target_date}) need a reminder.`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSendingReminders(false); }
  };

  const loadDocuments = async () => {
    const { data } = await api.get("/admin/documents");
    setDocuments(data);
  };
  const loadHospitals = async () => {
    const { data } = await api.get("/admin/hospitals");
    setHospitals(data);
  };
  const loadInsurers = async () => {
    const { data } = await api.get("/insurers");
    setInsurers(data);
  };
  const loadWhatsapp = async () => {
    const { data } = await api.get("/admin/whatsapp");
    setWhatsapp(data);
  };
  const loadFaqs = async () => {
    const { data } = await api.get("/admin/faqs");
    setFaqs(data);
  };
  const loadAdmins = async () => {
    const { data } = await api.get("/master/admins");
    setAdmins(data);
  };
  const loadWaLogs = async () => {
    try { const { data } = await api.get("/master/whatsapp/logs"); setWaLogs(data); }
    catch (err) { /* non-master */ }
  };
  const loadDigest = async () => {
    try { const { data } = await api.get("/master/digest"); setDigest(data); }
    catch (err) { /* non-master */ }
  };
  const loadDigestCfg = async () => {
    try { const { data } = await api.get("/master/digest/settings"); setDigestCfg(data); }
    catch (err) { /* non-master */ }
  };
  const saveDigestCfg = async () => {
    try {
      const { data } = await api.post("/master/digest/settings", digestCfg);
      setDigestCfg(data);
      toast.success("Digest schedule saved");
    } catch (err) { toast.error("Could not save schedule"); }
  };
  const sendDigest = async () => {
    try {
      const { data } = await api.get("/master/digest?send=true");
      setDigest(data);
      const c = data.channels_ready || {};
      if (!c.email && !c.whatsapp) toast.info("Digest generated. Add Resend/Twilio keys to actually deliver it.");
      else toast.success(`Digest sent (email: ${data.email_sent}, WhatsApp: ${data.whatsapp_sent}).`);
    } catch (err) { toast.error("Could not send digest"); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (tab === "documents") { loadDocuments(); load(); }
    if (tab === "network") loadHospitals();
    if (tab === "insurers" || tab === "network") loadInsurers();
    if (tab === "opd") loadOpdFaqs();
    if (tab === "whatsapp") { loadWhatsapp(); if (isMaster) loadWaLogs(); }
    if (tab === "faqs") loadFaqs();
    if (tab === "terms") loadPolicyTerms();
    if (tab === "admins") { loadAdmins(); loadDigest(); loadDigestCfg(); }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/claims/${id}`, { status });
      toast.success(`Claim marked ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const nudgeClaim = async (c) => {
    try {
      const { data } = await api.post(`/admin/claims/${c.id}/nudge`);
      toast.success(
        data.whatsapp_live
          ? `WhatsApp nudge sent (${data.used_template ? "template" : "text"}) — nudged ${data.nudge_count}×`
          : "Nudge logged (WhatsApp not live yet)"
      );
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addEmployee = async (e) => {
    e.preventDefault();
    try {
      const deps = (newEmp.dependents || []).filter((d) => (d.name || "").trim())
        .map((d) => ({ name: d.name.trim(), relation: d.relation || "Dependent", age: Number(d.age) || 0 }));
      await api.post("/admin/employees", { ...newEmp, sum_insured: Number(newEmp.sum_insured), opd_cover: newEmp.opd_cover !== "" ? Number(newEmp.opd_cover) : null, dependents: deps });
      toast.success(`${newEmp.name} added — welcome email sent`);
      setAddOpen(false);
      setNewEmp({ name: "", email: "", mobile: "", company: "", employee_code: "", uhid: "", policy_number: "", policy_expiry: "", opd_cover: "", dob: "", plan_name: "Silver Plan", sum_insured: 500000, dependents: [] });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const sendWelcome = async (emp) => {
    try {
      const { data } = await api.post(`/admin/employees/${emp.id}/welcome`);
      if (data.ok) toast.success(`Welcome email sent to ${data.sent_to}`);
      else if (!data.email_live) toast.info("Email not live yet — add your Resend key to send welcome emails.");
      else toast.error("Couldn't send — check the employee's email / Resend domain verification.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const welcomeAll = async () => {
    setWelcomingAll(true);
    try {
      const { data } = await api.post("/admin/employees/welcome-all");
      if (!data.email_live) toast.info("Email not live yet — add your Resend key first.");
      else toast.success(`Welcome emails: ${data.sent} sent, ${data.failed} failed, ${data.skipped_no_email} without email (of ${data.total}).`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setWelcomingAll(false);
    }
  };


  const uploadDocument = async (e, scope, userId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("scope", scope);
    if (userId) fd.append("user_id", userId);
    try {
      await api.post("/admin/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      loadDocuments();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    }
    e.target.value = "";
  };

  const deleteDocument = async (id) => {
    try {
      await api.delete(`/admin/documents/${id}`);
      toast.success("Document removed");
      loadDocuments();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove document");
    }
  };

  const uploadHospitalCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/hospitals/csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} hospitals`);
      loadHospitals();
    } catch (err) {
      toast.error("CSV import failed. Check the format.");
    }
    e.target.value = "";
  };

  const deleteHospital = async (id) => {
    try {
      await api.delete(`/admin/hospitals/${id}`);
      loadHospitals();
    } catch (err) {
      toast.error("Could not delete hospital");
    }
  };

  const [newHosp, setNewHosp] = useState({ name: "", city: "", area: "", specialties: "", phone: "", insurer: "", policy_number: "", cashless: true });
  const addHospital = async () => {
    if (!newHosp.name.trim()) { toast.error("Enter a hospital name."); return; }
    try {
      await api.post("/admin/hospitals", {
        ...newHosp,
        specialties: newHosp.specialties.split(";").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Hospital added");
      setNewHosp({ name: "", city: "", area: "", specialties: "", phone: "", insurer: "", policy_number: "", cashless: true });
      loadHospitals();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const addInsurer = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/insurers", newInsurer);
      toast.success(`${newInsurer.name} added`);
      setInsurerOpen(false);
      setNewInsurer({ name: "", type: "Insurer", contact: "", website: "" });
      loadInsurers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteInsurer = async (id) => {
    try {
      await api.delete(`/admin/insurers/${id}`);
      loadInsurers();
    } catch (err) {
      toast.error("Could not delete");
    }
  };

  const addDependent = async (e) => {
    e.preventDefault();
    if (!depEmp) return;
    try {
      const { data } = await api.post(`/admin/employees/${depEmp.id}/dependents`, { ...newDep, age: Number(newDep.age) });
      toast.success("Dependent added");
      setDepEmp({ ...depEmp, policy: { ...depEmp.policy, dependents: data } });
      setNewDep({ name: "", relation: "Spouse", age: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeDependent = async (depId) => {
    try {
      const { data } = await api.delete(`/admin/employees/${depEmp.id}/dependents/${depId}`);
      setDepEmp({ ...depEmp, policy: { ...depEmp.policy, dependents: data } });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove dependent");
    }
  };

  const saveWhatsapp = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/whatsapp", { provider: whatsapp.provider, number: whatsapp.number });
      setWhatsapp(data);
      toast.success("WhatsApp details saved. We'll connect it for you shortly.");
    } catch (err) {
      toast.error("Could not save");
    }
  };

  const addFaq = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/faqs", newFaq);
      toast.success("FAQ added");
      setFaqOpen(false);
      setNewFaq({ question: "", answer: "", category: "Coverage", product: "Group Health" });
      loadFaqs();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteFaq = async (id) => {
    try { await api.delete(`/admin/faqs/${id}`); loadFaqs(); }
    catch (err) { toast.error("Could not delete FAQ"); }
  };

  const importClaimsCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/claims/csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} claims${data.skipped ? `, skipped ${data.skipped}` : ""}`);
      load();
    } catch (err) {
      toast.error("Claims CSV import failed. Check the format.");
    }
    e.target.value = "";
  };

  const addAdmin = async (e) => {
    e.preventDefault();
    try {
      await api.post("/master/admins", newAdmin);
      toast.success(`${newAdmin.name} added`);
      setAdminOpen(false);
      setNewAdmin({ name: "", email: "", mobile: "", password: "", role: "admin" });
      loadAdmins();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteAdmin = async (id) => {
    try { await api.delete(`/master/admins/${id}`); loadAdmins(); toast.success("Admin removed"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove"); }
  };

  const changeRole = async (userId, role) => {
    try {
      await api.patch(`/admin/users/${userId}/role`, { role });
      toast.success(role === "admin" ? "Promoted to HR Admin" : role === "master_admin" ? "Promoted to Master Admin" : "Set as Employee");
      load(); if (isMaster) loadAdmins();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not change role"); }
  };
  const deleteUser = async (userId, name) => {
    if (!window.confirm(`Delete ${name || "this user"}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success("User deleted");
      load(); if (isMaster) loadAdmins();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete"); }
  };


  const importEmployeesCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    const fd = new FormData();
    fd.append("file", file);
    const tId = toast.loading("Importing employees… this can take a minute for large files.");
    try {
      const { data } = await api.post("/admin/employees/csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const jobId = data.job_id;
      if (!jobId) { // legacy sync response fallback
        toast.success(`Imported ${data.inserted} employees`, { id: tId }); load(); return;
      }
      // poll for completion
      let tries = 0;
      const poll = async () => {
        try {
          const { data: st } = await api.get(`/admin/employees/import-status/${jobId}`);
          if (st.status === "done") {
            toast.success(`Imported ${st.inserted} employees${st.dependents_added ? ` + ${st.dependents_added} family members` : ""}${st.merged ? `, merged ${st.merged} into existing families` : ""}${st.skipped ? `, skipped ${st.skipped} row(s)` : ""}`, { id: tId });
            if (st.skipped || st.merged) setLastSkipped({ job_id: jobId, count: (st.skipped || 0) + (st.merged || 0) });
            load(); return;
          }
          if (st.status === "failed") { toast.error(`Import failed: ${st.error || "unknown error"}`, { id: tId }); return; }
          if (tries++ > 150) { toast.error("Import is taking too long. Please refresh to see results.", { id: tId }); return; }
          setTimeout(poll, 2000);
        } catch (err) { toast.error("Couldn't check import status.", { id: tId }); }
      };
      setTimeout(poll, 2000);
    } catch (err) {
      toast.error("Employee import failed. Check the file format.", { id: tId });
    }
  };

  const hospitalTemplate = () =>
    downloadText("hospitals_template.csv",
      "name,city,area,address,specialties,cashless,phone,insurer,policy_number\nApollo Hospital,Bengaluru,Bannerghatta Road,154/11 Bannerghatta Rd,Cardiology;Oncology;Neurology,yes,080-2630-4050,Star Health,POL-1234567\n");

  const employeesTemplate = () =>
    downloadText("employees_template.csv",
      "employee_code,name,relation,email,mobile,dob,policy_number,policy_expiry,opd_cover,plan,sum_insured,company,uhid,password\n" +
      "EMP1001,Anita Rao,Self,anita@acme.com,9876543210,05-08-1990,POL-1234567,31-03-2027,15000,Gold Plan,1000000,Contoso India Pvt Ltd,UH1001,\n" +
      "EMP1001,Rahul Rao,Spouse,,,,,,,,,,,\n" +
      "EMP1001,Sara Rao,Daughter,,,,,,,,,,,\n");

  const claimsTemplate = () =>
    downloadText("claims_template.csv",
      "employee_name,member_id,policy_number,insurer,claim_type,hospital,treatment,amount,admission_date,status\nAnita Rao,IH-2026-90001,POL-1234567,Star Health,Cashless,Apollo Hospital,Fracture treatment,42000,10 Jun 2026,Submitted\n");

  const chartData = stats?.status_distribution || [];

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-60 bg-emerald-950 text-white p-5 shrink-0">
        <div className="mb-10">
          <Logo size={40} textClass="text-white text-base" />
        </div>
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-500 mb-3">Admin</p>
        <nav className="space-y-1 flex-1">
          {NAV.map((n) => (
            <button
              key={n.key}
              onClick={() => setTab(n.key)}
              data-testid={`nav-${n.key}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                tab === n.key ? "bg-emerald-800 text-white" : "text-emerald-200/70 hover:bg-emerald-900 hover:text-white"
              }`}
            >
              <n.icon size={18} weight={tab === n.key ? "fill" : "regular"} />
              {n.label}
            </button>
          ))}
        </nav>
        <div className="border-t border-white/10 pt-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-sm font-bold">
              {user?.name?.[0]}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-[11px] text-emerald-300/70 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={logout} data-testid="admin-logout"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-300 hover:bg-red-500/10 transition-colors">
            <SignOut size={16} /> Sign out
          </button>
          <p className="mt-4 text-[10px] text-emerald-300/50 leading-relaxed" data-testid="copyright">
            © {new Date().getFullYear()} Aarogya Innovate Pvt Ltd
          </p>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-6 sm:p-10 overflow-y-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-emerald-950 capitalize">{(BASE_NAV.find((n) => n.key === tab)?.label) || tab}</h1>
            <p className="text-sm text-zinc-500 mt-1">Group medical insurance · {user?.company || "Corporate"}</p>
          </div>
          {tab === "employees" && (
            <div className="flex items-center gap-2">
              <button data-testid="welcome-all-btn" onClick={welcomeAll} disabled={welcomingAll}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-300 text-emerald-800 text-sm font-medium px-4 py-2 hover:bg-emerald-50 transition-colors disabled:opacity-60">
                <EnvelopeSimple size={16} /> {welcomingAll ? "Sending…" : "Email all welcome"}
              </button>
              <button data-testid="employees-template" onClick={employeesTemplate}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-300 text-emerald-800 text-sm font-medium px-4 py-2 hover:bg-emerald-50 transition-colors">
                <DownloadSimple size={16} /> CSV template
              </button>
              <button data-testid="employees-template-xlsx" onClick={() => exportXlsx("/admin/employees/template.xlsx", "employees_family_template.xlsx")}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-300 text-emerald-800 text-sm font-medium px-4 py-2 hover:bg-emerald-50 transition-colors">
                <DownloadSimple size={16} /> Excel template
              </button>
              <label data-testid="import-employees-csv" className="cursor-pointer inline-flex items-center gap-1 rounded-full border border-emerald-300 text-emerald-800 text-sm font-medium px-4 py-2 hover:bg-emerald-50 transition-colors">
                <UploadSimple size={16} /> Import CSV / Excel
                <input type="file" className="hidden" accept=".csv,.xlsx,.xlsm" onChange={importEmployeesCsv} />
              </label>
              <button data-testid="onboarding-blast-btn" onClick={openOnboard}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-300 text-emerald-800 text-sm font-medium px-4 py-2 hover:bg-emerald-50 transition-colors">
                <PaperPlaneTilt size={16} /> Onboarding blast
              </button>
              <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
                <DialogTrigger asChild>
                  <button data-testid="bulk-policy-btn"
                    className="inline-flex items-center gap-1 rounded-full border border-emerald-300 text-emerald-800 text-sm font-medium px-4 py-2 hover:bg-emerald-50 transition-colors">
                    <Buildings size={16} /> Set common policy
                  </button>
                </DialogTrigger>
                <DialogContent data-testid="bulk-policy-dialog">
                  <DialogHeader><DialogTitle>Set common policy details</DialogTitle></DialogHeader>
                  <p className="text-xs text-zinc-500 -mt-2">Push the same Policy Number, Expiry and Sum Insured to many employees at once. This <b>overwrites</b> the fields you fill in (leave a field blank to keep it unchanged). Dependents inherit the policy number.</p>
                  <div className="space-y-3 mt-2">
                    <div className="space-y-1.5">
                      <Label>Apply to</Label>
                      <select data-testid="bulk-company" value={bulkForm.company}
                        onChange={(e) => setBulkForm({ ...bulkForm, company: e.target.value })}
                        className="w-full h-10 rounded-md border border-zinc-300 bg-white px-3 text-sm">
                        <option value="all">All companies</option>
                        {[...new Set(employees.map((e) => e.company).filter(Boolean))].sort().map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Policy number</Label>
                      <Input data-testid="bulk-policy-number" value={bulkForm.policy_number}
                        onChange={(e) => setBulkForm({ ...bulkForm, policy_number: e.target.value })} placeholder="POL-1234567" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label>Policy expiry <span className="text-zinc-400 text-xs">(DDMMYYYY)</span></Label>
                        <Input data-testid="bulk-policy-expiry" value={bulkForm.policy_expiry}
                          onChange={(e) => setBulkForm({ ...bulkForm, policy_expiry: e.target.value })} placeholder="31-03-2027" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Sum insured (₹)</Label>
                        <Input data-testid="bulk-sum-insured" type="number" value={bulkForm.sum_insured}
                          onChange={(e) => setBulkForm({ ...bulkForm, sum_insured: e.target.value })} placeholder="500000" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Plan tier</Label>
                      <select data-testid="bulk-plan" value={bulkForm.plan_name}
                        onChange={(e) => setBulkForm({ ...bulkForm, plan_name: e.target.value })}
                        className="w-full h-10 rounded-md border border-zinc-300 bg-white px-3 text-sm">
                        <option value="">— Keep unchanged —</option>
                        <option value="Silver Plan">Silver Plan</option>
                        <option value="Gold Plan">Gold Plan</option>
                        <option value="Platinum Plan">Platinum Plan</option>
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>OPD cover (₹)</Label>
                      <Input data-testid="bulk-opd-cover" type="number" value={bulkForm.opd_cover}
                        onChange={(e) => setBulkForm({ ...bulkForm, opd_cover: e.target.value })} placeholder="15000 (leave blank to keep unchanged)" />
                    </div>
                    <Button data-testid="bulk-apply-btn" onClick={applyBulkPolicy} disabled={bulkSaving}
                      className="w-full rounded-full bg-emerald-900 hover:bg-emerald-800 text-white">
                      {bulkSaving ? "Applying…" : "Apply to employees"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              {bulkUndo.available && (
                <button data-testid="bulk-undo-btn" onClick={undoBulkPolicy}
                  title={`Undo the last bulk policy update (${bulkUndo.count} employees${bulkUndo.company && bulkUndo.company !== "all" ? `, ${bulkUndo.company}` : ""})`}
                  className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 text-amber-800 text-sm font-medium px-4 py-2 hover:bg-amber-100 transition-colors">
                  <Clock size={16} /> Undo last policy update
                </button>
              )}
              {isMaster && selectedEmps.size > 0 && (
                <button data-testid="delete-selected-btn" onClick={deleteSelectedEmps}
                  className="inline-flex items-center gap-1 rounded-full border border-red-300 bg-red-50 text-red-700 text-sm font-medium px-4 py-2 hover:bg-red-100 transition-colors">
                  <Trash size={16} /> Delete selected ({selectedEmps.size})
                </button>
              )}
              {lastSkipped && (
                <button data-testid="download-skipped-btn"
                  onClick={() => exportXlsx(`/admin/employees/import-skipped/${lastSkipped.job_id}`, "import_skipped_rows.xlsx")}
                  className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 text-amber-800 text-sm font-medium px-4 py-2 hover:bg-amber-100 transition-colors">
                  <DownloadSimple size={16} /> Import report ({lastSkipped.count})
                </button>
              )}
              <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-employee-btn" className="rounded-full bg-emerald-900 hover:bg-emerald-800 text-white transition-colors">
                  <Plus size={18} className="mr-1" /> Add employee
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add employee</DialogTitle></DialogHeader>
                <form onSubmit={addEmployee} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label>Full name</Label>
                    <Input required value={newEmp.name} data-testid="emp-name"
                      onChange={(e) => setNewEmp({ ...newEmp, name: e.target.value })} placeholder="Anita Rao" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Email</Label>
                    <Input required type="email" value={newEmp.email} data-testid="emp-email"
                      onChange={(e) => setNewEmp({ ...newEmp, email: e.target.value })} placeholder="anita@acme.com" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Mobile number</Label>
                    <Input value={newEmp.mobile} data-testid="emp-mobile"
                      onChange={(e) => setNewEmp({ ...newEmp, mobile: e.target.value })} placeholder="9876543210" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Company</Label>
                    <Input value={newEmp.company || ""} data-testid="emp-company"
                      onChange={(e) => setNewEmp({ ...newEmp, company: e.target.value })} placeholder="e.g. Secon Pvt Ltd" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Employee code</Label>
                      <Input value={newEmp.employee_code} data-testid="emp-code"
                        onChange={(e) => setNewEmp({ ...newEmp, employee_code: e.target.value })} placeholder="EMP1001" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>UHID <span className="text-zinc-400 text-xs">(shows on e-card)</span></Label>
                      <Input value={newEmp.uhid} data-testid="emp-uhid"
                        onChange={(e) => setNewEmp({ ...newEmp, uhid: e.target.value })} placeholder="UH1001" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Policy number</Label>
                      <Input value={newEmp.policy_number} data-testid="emp-policy-number"
                        onChange={(e) => setNewEmp({ ...newEmp, policy_number: e.target.value })} placeholder="POL-1234567" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Date of birth <span className="text-zinc-400 text-xs">(sets web password, DDMMYYYY)</span></Label>
                      <Input value={newEmp.dob} data-testid="emp-dob"
                        onChange={(e) => setNewEmp({ ...newEmp, dob: e.target.value })} placeholder="05-08-1990" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Policy expiry <span className="text-zinc-400 text-xs">(valid till, DDMMYYYY)</span></Label>
                      <Input value={newEmp.policy_expiry} data-testid="emp-policy-expiry"
                        onChange={(e) => setNewEmp({ ...newEmp, policy_expiry: e.target.value })} placeholder="31-03-2027" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>OPD cover (₹) <span className="text-zinc-400 text-xs">(optional)</span></Label>
                      <Input type="number" value={newEmp.opd_cover} data-testid="emp-opd-cover"
                        onChange={(e) => setNewEmp({ ...newEmp, opd_cover: e.target.value })} placeholder="15000" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Plan</Label>
                      <Select value={newEmp.plan_name} onValueChange={(v) => setNewEmp({ ...newEmp, plan_name: v })}>
                        <SelectTrigger data-testid="emp-plan"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Silver Plan">Silver Plan</SelectItem>
                          <SelectItem value="Gold Plan">Gold Plan</SelectItem>
                          <SelectItem value="Platinum Plan">Platinum Plan</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Sum insured (₹)</Label>
                      <Input type="number" value={newEmp.sum_insured} data-testid="emp-sum"
                        onChange={(e) => setNewEmp({ ...newEmp, sum_insured: e.target.value })} />
                    </div>
                  </div>
                  <p className="text-xs text-zinc-500">Login password: the employee's <b>Date of birth (DDMMYYYY)</b> if provided, else <span className="font-mono">employee123</span>.</p>
                  <div className="space-y-2 border-t border-zinc-100 pt-3" data-testid="emp-dependents-editor">
                    <Label>Family / dependents <span className="text-zinc-400 text-xs">(mapped to this employee)</span></Label>
                    {(newEmp.dependents || []).map((d, i) => (
                      <div key={i} className="grid grid-cols-12 gap-1.5">
                        <Input data-testid={`emp-dep-name-${i}`} value={d.name} placeholder="Full name" className="col-span-5 h-9"
                          onChange={(e) => setNewEmp((p) => { const ds = [...p.dependents]; ds[i] = { ...ds[i], name: e.target.value }; return { ...p, dependents: ds }; })} />
                        <select data-testid={`emp-dep-relation-${i}`} value={d.relation} className="col-span-4 h-9 rounded-md border border-zinc-200 px-2 text-sm"
                          onChange={(e) => setNewEmp((p) => { const ds = [...p.dependents]; ds[i] = { ...ds[i], relation: e.target.value }; return { ...p, dependents: ds }; })}>
                          {["Spouse", "Son", "Daughter", "Father", "Mother", "Other"].map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                        <Input data-testid={`emp-dep-age-${i}`} value={d.age} placeholder="Age" className="col-span-2 h-9"
                          onChange={(e) => setNewEmp((p) => { const ds = [...p.dependents]; ds[i] = { ...ds[i], age: e.target.value }; return { ...p, dependents: ds }; })} />
                        <button type="button" data-testid={`emp-dep-remove-${i}`} className="col-span-1 text-red-400 hover:text-red-600 text-sm"
                          onClick={() => setNewEmp((p) => ({ ...p, dependents: p.dependents.filter((_, idx) => idx !== i) }))}>×</button>
                      </div>
                    ))}
                    <button type="button" data-testid="emp-add-dependent"
                      onClick={() => setNewEmp((p) => ({ ...p, dependents: [...(p.dependents || []), { name: "", relation: "Spouse", age: "" }] }))}
                      className="text-xs font-medium text-emerald-700 hover:underline">+ Add family member</button>
                  </div>
                  <DialogFooter>
                    <Button type="submit" data-testid="emp-submit" className="rounded-full bg-emerald-900 hover:bg-emerald-800 text-white">
                      Add employee
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          )}
          {tab === "claims" && (
            <div className="flex items-center gap-2">
              <button
                data-testid="filter-flagged-claims"
                onClick={() => setShowFlaggedOnly((v) => !v)}
                className={`inline-flex items-center gap-1 rounded-full text-sm font-medium px-4 py-2 transition-colors border ${
                  showFlaggedOnly
                    ? "bg-amber-500 border-amber-500 text-white hover:bg-amber-600"
                    : "border-amber-300 text-amber-800 hover:bg-amber-50"
                }`}
              >
                ⚠ {showFlaggedOnly ? "Showing flagged" : "Needs review"}
                {flaggedCount > 0 && (
                  <span className={`ml-1 rounded-full px-1.5 text-[11px] font-semibold ${showFlaggedOnly ? "bg-white/25 text-white" : "bg-amber-100 text-amber-800"}`}>
                    {flaggedCount}
                  </span>
                )}
              </button>
              <button data-testid="claims-template" onClick={claimsTemplate}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-300 text-emerald-800 text-sm font-medium px-4 py-2 hover:bg-emerald-50 transition-colors">
                <DownloadSimple size={16} /> CSV template
              </button>
              <label data-testid="import-claims-csv" className="cursor-pointer inline-flex items-center gap-1 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white text-sm font-medium px-4 py-2 transition-colors">
                <UploadSimple size={18} /> Import claims CSV
                <input type="file" className="hidden" accept=".csv" onChange={importClaimsCsv} />
              </label>
            </div>
          )}
          {tab === "faqs" && (
            <Dialog open={faqOpen} onOpenChange={setFaqOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-faq-btn" className="rounded-full bg-emerald-900 hover:bg-emerald-800 text-white transition-colors">
                  <Plus size={18} className="mr-1" /> Add FAQ
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add FAQ</DialogTitle></DialogHeader>
                <form onSubmit={addFaq} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Product</Label>
                      <Select value={newFaq.product} onValueChange={(v) => setNewFaq({ ...newFaq, product: v })}>
                        <SelectTrigger data-testid="faq-product"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Group Health">Group Health</SelectItem>
                          <SelectItem value="Group Term Life">Group Term Life</SelectItem>
                          <SelectItem value="Group Personal Accident">Group Personal Accident</SelectItem>
                          <SelectItem value="Individual Health">Individual Health</SelectItem>
                          <SelectItem value="All Products">All Products</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Category</Label>
                      <Select value={newFaq.category} onValueChange={(v) => setNewFaq({ ...newFaq, category: v })}>
                        <SelectTrigger data-testid="faq-category"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Coverage">Coverage</SelectItem>
                          <SelectItem value="Claims">Claims</SelectItem>
                          <SelectItem value="Policy">Policy</SelectItem>
                          <SelectItem value="Support">Support</SelectItem>
                          <SelectItem value="General">General</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Question</Label>
                    <Input required value={newFaq.question} data-testid="faq-question"
                      onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })} placeholder="Is maternity covered?" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Answer</Label>
                    <textarea required value={newFaq.answer} data-testid="faq-answer" rows={3}
                      onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
                      className="w-full rounded-md border border-zinc-200 p-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Yes, up to ₹50,000…" />
                  </div>
                  <DialogFooter>
                    <Button type="submit" data-testid="faq-submit" className="rounded-full bg-emerald-900 hover:bg-emerald-800 text-white">Add FAQ</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
          {tab === "admins" && (
            <Dialog open={adminOpen} onOpenChange={setAdminOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-admin-btn" className="rounded-full bg-emerald-900 hover:bg-emerald-800 text-white transition-colors">
                  <Plus size={18} className="mr-1" /> Add admin
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add admin account</DialogTitle></DialogHeader>
                <form onSubmit={addAdmin} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label>Full name</Label>
                    <Input required value={newAdmin.name} data-testid="admin-name"
                      onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} placeholder="Priya Menon" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Email</Label>
                      <Input required type="email" value={newAdmin.email} data-testid="admin-email"
                        onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })} placeholder="priya@company.com" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Mobile</Label>
                      <Input value={newAdmin.mobile} data-testid="admin-mobile"
                        onChange={(e) => setNewAdmin({ ...newAdmin, mobile: e.target.value })} placeholder="9876543210" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Password</Label>
                      <Input required type="text" value={newAdmin.password} data-testid="admin-password"
                        onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })} placeholder="Set a password" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Role</Label>
                      <Select value={newAdmin.role} onValueChange={(v) => setNewAdmin({ ...newAdmin, role: v })}>
                        <SelectTrigger data-testid="admin-role"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">HR Admin</SelectItem>
                          <SelectItem value="master_admin">Master Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="submit" data-testid="admin-submit" className="rounded-full bg-emerald-900 hover:bg-emerald-800 text-white">Create account</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
          {tab === "documents" && (
            <label data-testid="upload-company-doc" className="cursor-pointer inline-flex items-center gap-1 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white text-sm font-medium px-4 py-2 transition-colors">
              <UploadSimple size={18} /> Upload company policy
              <input type="file" className="hidden" accept=".pdf,.doc,.docx,.png,.jpg" onChange={(e) => uploadDocument(e, "company", null)} />
            </label>
          )}
          {tab === "network" && (
            <div className="flex items-center gap-2">
              <button data-testid="hospitals-template" onClick={hospitalTemplate}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-300 text-emerald-800 text-sm font-medium px-4 py-2 hover:bg-emerald-50 transition-colors">
                <DownloadSimple size={16} /> CSV template
              </button>
              <label data-testid="import-csv" className="cursor-pointer inline-flex items-center gap-1 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white text-sm font-medium px-4 py-2 transition-colors">
                <UploadSimple size={18} /> Import hospitals CSV
                <input type="file" className="hidden" accept=".csv" onChange={uploadHospitalCsv} />
              </label>
            </div>
          )}
          {tab === "insurers" && (
            <Dialog open={insurerOpen} onOpenChange={setInsurerOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-insurer-btn" className="rounded-full bg-emerald-900 hover:bg-emerald-800 text-white transition-colors">
                  <Plus size={18} className="mr-1" /> Add insurer / TPA
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add insurer / TPA</DialogTitle></DialogHeader>
                <form onSubmit={addInsurer} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label>Name</Label>
                    <Input required value={newInsurer.name} data-testid="insurer-name"
                      onChange={(e) => setNewInsurer({ ...newInsurer, name: e.target.value })} placeholder="Star Health" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Type</Label>
                      <Select value={newInsurer.type} onValueChange={(v) => setNewInsurer({ ...newInsurer, type: v })}>
                        <SelectTrigger data-testid="insurer-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Insurer">Insurer</SelectItem>
                          <SelectItem value="TPA">TPA</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Contact</Label>
                      <Input value={newInsurer.contact} data-testid="insurer-contact"
                        onChange={(e) => setNewInsurer({ ...newInsurer, contact: e.target.value })} placeholder="1800-000-000" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Website</Label>
                    <Input value={newInsurer.website} data-testid="insurer-website"
                      onChange={(e) => setNewInsurer({ ...newInsurer, website: e.target.value })} placeholder="example.com" />
                  </div>
                  <DialogFooter>
                    <Button type="submit" data-testid="insurer-submit" className="rounded-full bg-emerald-900 hover:bg-emerald-800 text-white">Add</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* OVERVIEW */}
        {tab === "overview" && stats && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Metric icon={Users} label="Employees covered" value={stats.total_employees} sub="Active members" />
              <Metric icon={ClipboardText} label="Total claims" value={stats.total_claims} tone="blue" />
              <Metric icon={Clock} label="Pending review" value={stats.pending_claims} tone="amber" />
              <Metric icon={CheckCircle} label="Approved / settled" value={stats.approved_claims} />
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 rounded-xl border border-zinc-200 bg-white p-6 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
                <h3 className="font-heading font-medium text-emerald-900 mb-6">Claims by status</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="status" tick={{ fontSize: 11, fill: "#71717a" }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#71717a" }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: "#f4f4f5" }} />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                      {chartData.map((_, i) => <Cell key={i} fill={barColors[i % barColors.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="rounded-xl border border-zinc-200 bg-white p-6 shadow-[0_2px_10px_rgba(0,0,0,0.02)] flex flex-col gap-4">
                <h3 className="font-heading font-medium text-emerald-900">Claim value</h3>
                <div className="rounded-lg bg-emerald-950 text-white p-5">
                  <div className="flex items-center gap-2 text-emerald-300 text-xs uppercase tracking-widest">
                    <CurrencyInr size={14} /> Total claimed
                  </div>
                  <p className="font-mono text-2xl mt-2">{inr(stats.total_claim_amount)}</p>
                </div>
                <div className="rounded-lg bg-emerald-50 p-5">
                  <div className="flex items-center gap-2 text-emerald-700 text-xs uppercase tracking-widest">
                    <TrendUp size={14} /> Approved value
                  </div>
                  <p className="font-mono text-2xl mt-2 text-emerald-900">{inr(stats.approved_amount)}</p>
                </div>
              </div>
            </div>

            {stats.lead_insights && (
              <div className="rounded-xl border border-zinc-200 bg-white p-6 shadow-[0_2px_10px_rgba(0,0,0,0.02)]" data-testid="lead-insights-card">
                <h3 className="font-heading font-medium text-emerald-900">OPD & Health check-up demand</h3>
                <p className="text-sm text-zinc-500 mb-4">Enquiries vs enrollments — a quick read on how much interest each is getting.</p>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={stats.lead_insights}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#71717a" }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#71717a" }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: "#f4f4f5" }} />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                      {stats.lead_insights.map((_, i) => <Cell key={i} fill={barColors[i % barColors.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </motion.div>
        )}

        {/* CLAIMS */}
        {tab === "claims" && (
          <div className="rounded-xl border border-zinc-200 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.02)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="claims-table">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                    <th className="px-5 py-3">Claim</th>
                    <th className="px-5 py-3">Employee</th>
                    <th className="px-5 py-3">Policy / Insurer</th>
                    <th className="px-5 py-3">Hospital / Treatment</th>
                    <th className="px-5 py-3 text-right">Amount</th>
                    <th className="px-5 py-3">Bill</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleClaims.map((c) => (
                    <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                      <td className="px-5 py-4 font-mono text-xs text-zinc-600">{c.claim_number}</td>
                      <td className="px-5 py-4 font-medium text-zinc-800">{c.employee_name}</td>
                      <td className="px-5 py-4">
                        <p className="font-mono text-xs text-zinc-600">{c.policy_number || "—"}</p>
                        <p className="text-xs text-emerald-700">{c.insurer || "—"}</p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-zinc-800">{c.treatment}</p>
                        <p className="text-xs text-zinc-400">{c.hospital}</p>
                      </td>
                      <td className="px-5 py-4 text-right font-mono font-medium text-emerald-800">
                        {inr(c.amount)}
                        {c.review_flag && (
                          <span
                            data-testid={`claim-mismatch-${c.claim_number}`}
                            title={c.ocr_amount ? `Bill read as ${inr(c.ocr_amount)} — entered amount differs by >20%` : "Amount mismatch — review"}
                            className="ml-2 inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 align-middle"
                          >
                            ⚠ Review
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        {c.bill_file_id ? (
                          <img
                            src={billUrl(c.id)}
                            alt="Bill"
                            data-testid={`claim-bill-thumb-${c.claim_number}`}
                            onClick={() => setBillPreview(billUrl(c.id))}
                            className="h-12 w-12 rounded-md object-cover border border-zinc-200 cursor-pointer hover:ring-2 hover:ring-emerald-400 transition"
                          />
                        ) : c.document_url ? (
                          <a
                            href={c.document_url}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`claim-bill-link-${c.claim_number}`}
                            className="text-xs text-emerald-700 underline"
                          >
                            View
                          </a>
                        ) : (
                          <span className="text-xs text-zinc-300">—</span>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${statusColors[c.status]}`}>{c.status}</span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <Select value={c.status} onValueChange={(v) => updateStatus(c.id, v)}>
                            <SelectTrigger className="w-36 h-9" data-testid={`claim-status-${c.claim_number}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          {c.review_flag && (
                            <div className="flex flex-col items-start gap-1">
                              <button
                                data-testid={`claim-nudge-${c.claim_number}`}
                                onClick={() => nudgeClaim(c)}
                                title="Ask the member on WhatsApp to correct the bill/amount"
                                className="inline-flex items-center gap-1 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium px-3 py-1.5 transition-colors whitespace-nowrap"
                              >
                                <WhatsappLogo size={14} weight="fill" />
                                {c.nudge_count ? "Nudge again" : "Nudge"}
                              </button>
                              {c.nudge_count > 0 && (
                                <span data-testid={`claim-nudge-history-${c.claim_number}`} className="text-[10px] text-zinc-500 whitespace-nowrap">
                                  Nudged {c.nudge_count}×{c.last_nudged_at ? ` · ${new Date(c.last_nudged_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {visibleClaims.length === 0 && (
                    <tr><td colSpan={8} className="px-5 py-10 text-center text-zinc-400">{showFlaggedOnly ? "No flagged claims — all good." : "No claims yet."}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {billPreview && (
          <div
            data-testid="bill-preview-overlay"
            onClick={() => setBillPreview(null)}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6"
          >
            <div className="relative max-h-[90vh] max-w-3xl" onClick={(e) => e.stopPropagation()}>
              <button
                data-testid="bill-preview-close"
                onClick={() => setBillPreview(null)}
                className="absolute -right-3 -top-3 h-8 w-8 rounded-full bg-white text-zinc-800 shadow flex items-center justify-center hover:bg-zinc-100"
              >
                ✕
              </button>
              <img src={billPreview} alt="Bill full" className="max-h-[90vh] max-w-full rounded-lg object-contain bg-white" />
            </div>
          </div>
        )}

        {/* SERVICE TICKETS */}
        {tab === "tickets" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading text-xl font-semibold text-emerald-950">Service Tickets</h2>
                <p className="text-sm text-zinc-500">{tickets.length} ticket(s) raised by members via web & WhatsApp.</p>
              </div>
              <button data-testid="export-tickets" onClick={() => exportXlsx("/admin/tickets/export", "insurehub_tickets.xlsx")}
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white text-sm font-medium px-4 py-2 transition-colors">
                <FileXls size={16} /> Export Excel
              </button>
            </div>
            <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm" data-testid="ticket-routing-card">
              <p className="font-heading font-semibold text-emerald-950 text-sm">Auto-routing & team notifications</p>
              <p className="text-xs text-zinc-500 mb-3">New tickets are routed by service reason and the team is emailed automatically. Leave blank to skip that team.</p>
              <div className="grid gap-3 md:grid-cols-3">
                <div className="space-y-1">
                  <label className="text-xs text-zinc-500">Cashless team email</label>
                  <Input data-testid="cashless-team-email" value={cashlessEmail} onChange={(e) => setCashlessEmail(e.target.value)}
                    onBlur={() => saveTeamSettings({ cashless_team_email: cashlessEmail })} placeholder="cashless@team.com" className="h-9" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-zinc-500">Reimbursement team email</label>
                  <Input data-testid="reimbursement-team-email" value={reimbEmail} onChange={(e) => setReimbEmail(e.target.value)}
                    onBlur={() => saveTeamSettings({ reimbursement_team_email: reimbEmail })} placeholder="reimburse@team.com" className="h-9" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-zinc-500">General support email (fallback)</label>
                  <Input data-testid="support-team-email" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)}
                    onBlur={() => saveTeamSettings({ support_team_email: supportEmail })} placeholder="support@team.com" className="h-9" />
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-zinc-200 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.02)] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
                    <tr>
                      <th className="px-5 py-3">Ticket #</th>
                      <th className="px-5 py-3">Employee</th>
                      <th className="px-5 py-3">Contact</th>
                      <th className="px-5 py-3">Category</th>
                      <th className="px-5 py-3">Reason / Claim</th>
                      <th className="px-5 py-3">Description</th>
                      <th className="px-5 py-3">Waiting / SLA</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3">Resolution note</th>
                      <th className="px-5 py-3">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map((t) => (
                      <tr key={t.id} data-testid={`ticket-row-${t.ticket_number}`} className="border-t border-zinc-100 align-top">
                        <td className="px-5 py-4 font-mono text-xs text-emerald-800">{t.ticket_number}<div className="text-[10px] text-zinc-400">{(t.created_at || "").slice(0, 10)}</div></td>
                        <td className="px-5 py-4">{t.employee_name}<div className="text-[10px] text-zinc-400">{t.member_id}</div></td>
                        <td className="px-5 py-4 text-xs text-zinc-600">{t.mobile || "—"}<div className="text-[10px] text-zinc-400">{t.email}</div></td>
                        <td className="px-5 py-4">{t.category}</td>
                        <td className="px-5 py-4 text-xs">
                          {t.service_reason ? <span className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">{t.service_reason}</span> : <span className="text-zinc-300">—</span>}
                          {t.claim_number ? <div className="text-[10px] text-zinc-500 mt-1 font-mono">{t.claim_number}</div> : null}
                        </td>
                        <td className="px-5 py-4 max-w-xs text-zinc-600">{t.description}</td>
                        <td className="px-5 py-4">
                          {(() => { const a = ticketAgeInfo(t.created_at, t.status); return (
                            <span data-testid={`ticket-sla-${t.ticket_number}`}
                              className={`inline-block text-[11px] font-semibold px-2 py-1 rounded-full ${a.breached ? "bg-red-100 text-red-700" : t.status === "Resolved" ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
                              {a.breached ? `⚠ ${a.text} · SLA breached` : `${a.text}`}
                            </span>
                          ); })()}
                        </td>
                        <td className="px-5 py-4">
                          <Select value={t.status} onValueChange={(v) => updateTicket(t.id, { status: v })}>
                            <SelectTrigger className="w-32 h-9" data-testid={`ticket-status-${t.ticket_number}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{["Open", "In Progress", "Resolved"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="px-5 py-4">
                          <Input data-testid={`ticket-note-${t.ticket_number}`} defaultValue={t.resolution_note || ""} placeholder="Add note…"
                            className="h-9 w-44 text-xs" onBlur={(e) => { if (e.target.value !== (t.resolution_note || "")) updateTicket(t.id, { resolution_note: e.target.value }); }} />
                        </td>
                        <td className="px-5 py-4"><span className="text-[10px] uppercase text-zinc-400">{t.source}</span></td>
                      </tr>
                    ))}
                    {tickets.length === 0 && (<tr><td colSpan={10} className="px-5 py-10 text-center text-zinc-400">No tickets yet.</td></tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* HEALTH CHECK-UP BOOKINGS */}
        {tab === "bookings" && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-heading text-xl font-semibold text-emerald-950">Home Health Check-ups</h2>
                <p className="text-sm text-zinc-500">{bookings.length} booking(s) — home collection by Lupin Diagnostics.</p>
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1.5 text-xs text-zinc-600 mr-1 cursor-pointer" data-testid="auto-email-toggle" onClick={toggleAutoEmail}>
                  <span className={`relative inline-block h-5 w-9 rounded-full transition-colors ${autoEmailVendor ? "bg-emerald-600" : "bg-zinc-300"}`}>
                    <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${autoEmailVendor ? "left-4" : "left-0.5"}`} />
                  </span>
                  Auto-email Lupin
                </label>
                <Input data-testid="vendor-email-input" value={vendorEmail} onChange={(e) => setVendorEmail(e.target.value)}
                  onBlur={() => saveVendorSettings({ vendor_email: vendorEmail })}
                  placeholder="Lupin vendor email" className="h-9 w-52 text-sm" />
                <button data-testid="email-vendor" onClick={emailVendor} disabled={emailingVendor}
                  className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 text-emerald-800 text-sm font-medium px-4 py-2 hover:bg-emerald-50 transition-colors disabled:opacity-60">
                  <EnvelopeSimple size={16} /> {emailingVendor ? "Sending…" : "Email to Lupin"}
                </button>
                <button data-testid="send-reminders" onClick={sendReminders} disabled={sendingReminders}
                  className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 text-emerald-800 text-sm font-medium px-4 py-2 hover:bg-emerald-50 transition-colors disabled:opacity-60">
                  <Bell size={16} /> {sendingReminders ? "Sending…" : "Send reminders"}
                </button>
                <button data-testid="export-bookings" onClick={() => exportXlsx("/admin/bookings/export", "home_collection_bookings.xlsx")}
                  className="inline-flex items-center gap-1.5 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white text-sm font-medium px-4 py-2 transition-colors">
                  <FileXls size={16} /> Export Excel
                </button>
              </div>
            </div>
            <div className="rounded-xl border border-zinc-200 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.02)] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
                    <tr>
                      <th className="px-5 py-3">Booking #</th>
                      <th className="px-5 py-3">Patient</th>
                      <th className="px-5 py-3">Address</th>
                      <th className="px-5 py-3">Preferred</th>
                      <th className="px-5 py-3">Payment</th>
                      <th className="px-5 py-3">Slip</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3">Report</th>
                      <th className="px-5 py-3">Vendor remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((b) => (
                      <tr key={b.id} data-testid={`booking-row-${b.booking_number}`} className="border-t border-zinc-100 align-top">
                        <td className="px-5 py-4 font-mono text-xs text-emerald-800">{b.booking_number}<div className="text-[10px] text-zinc-400">{(b.created_at || "").slice(0, 10)}</div></td>
                        <td className="px-5 py-4">{b.name}<div className="text-[10px] text-zinc-400">{b.phone} · Age {b.age || "—"}</div></td>
                        <td className="px-5 py-4 max-w-[200px] text-zinc-600">{b.address}<div className="text-[10px] text-zinc-400">Pin {b.pincode}</div></td>
                        <td className="px-5 py-4 text-zinc-600">{b.preferred_date}<div className="text-[10px] text-zinc-400">{b.preferred_time}</div></td>
                        <td className="px-5 py-4">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${b.payment_made ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-500"}`}>{b.payment_made ? "Paid" : "Unpaid"}</span>
                          {b.payment_details ? <div className="text-[10px] text-zinc-400 mt-1">{b.payment_details}</div> : null}
                        </td>
                        <td className="px-5 py-4">
                          {b.payment_screenshot_file_id ? (
                            <img src={shotUrl(b.id)} alt="slip" data-testid={`booking-slip-${b.booking_number}`}
                              onClick={() => setShotPreview(shotUrl(b.id))}
                              className="h-11 w-11 rounded-md object-cover border border-zinc-200 cursor-pointer hover:ring-2 hover:ring-emerald-400 transition" />
                          ) : <span className="text-xs text-zinc-300">—</span>}
                        </td>
                        <td className="px-5 py-4">
                          <Select value={b.status} onValueChange={(v) => updateBooking(b.id, { status: v })}>
                            <SelectTrigger className="w-32 h-9" data-testid={`booking-status-${b.booking_number}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{["Requested", "Scheduled", "Completed", "Cancelled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="px-5 py-4">
                          {b.report_file_id ? (
                            <button data-testid={`admin-download-report-${b.booking_number}`}
                              onClick={() => downloadReport(b.id, b.report_filename)}
                              className="text-xs font-medium text-emerald-700 hover:underline">↓ Report</button>
                          ) : (
                            <label className="text-xs text-emerald-700 hover:underline cursor-pointer" data-testid={`admin-upload-report-${b.booking_number}`}>
                              Upload
                              <input type="file" accept=".pdf,image/*" className="hidden"
                                onChange={(e) => { uploadReport(b.id, e.target.files?.[0]); e.target.value = ""; }} />
                            </label>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <Input data-testid={`booking-remark-${b.booking_number}`} defaultValue={b.vendor_remarks || ""} placeholder="Lupin's remarks…"
                            className="h-9 w-44 text-xs" onBlur={(e) => { if (e.target.value !== (b.vendor_remarks || "")) updateBooking(b.id, { vendor_remarks: e.target.value }); }} />
                        </td>
                      </tr>
                    ))}
                    {bookings.length === 0 && (<tr><td colSpan={9} className="px-5 py-10 text-center text-zinc-400">No bookings yet.</td></tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* OPD PLANS */}
        {tab === "opd" && (
          <div className="space-y-6" data-testid="opd-tab">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading text-xl font-semibold text-emerald-950">OPD Plans</h2>
                <p className="text-sm text-zinc-500">Set the premium employees pay. Individual & Family Floater (2A+4C) prices are editable. Set to 0 to hide the buy button.</p>
              </div>
              <button data-testid="export-opd-enrollments" onClick={() => exportXlsx("/admin/opd/enrollments/export", "opd_enrollments.xlsx")}
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white text-sm font-medium px-4 py-2 transition-colors">
                <FileXls size={16} /> Export OPD enrollments
              </button>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {opdPlans.map((p) => (
                <div key={p.id} data-testid={`admin-opd-${p.name}`} className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center justify-between">
                    <p className="font-heading font-semibold text-emerald-950">{p.name} OPD</p>
                    <label className="flex items-center gap-1.5 text-[11px] text-zinc-500 cursor-pointer" onClick={() => updateOpdPlan(p.id, { active: !p.active })}>
                      <span className={`relative inline-block h-4 w-8 rounded-full transition-colors ${p.active ? "bg-emerald-600" : "bg-zinc-300"}`}>
                        <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${p.active ? "left-4" : "left-0.5"}`} />
                      </span>{p.active ? "Active" : "Hidden"}
                    </label>
                  </div>
                  <p className="text-xs text-zinc-500 mt-0.5">Cover {inr(p.cover_amount)} · {(p.benefits || []).length} benefits</p>
                  <div className="mt-3 space-y-2">
                    <label className="text-xs text-zinc-500">Individual premium (₹)</label>
                    <Input type="number" data-testid={`opd-price-individual-${p.name}`} defaultValue={p.price_individual || 0} className="h-9"
                      onBlur={(e) => { const v = Number(e.target.value); if (v !== (p.price_individual || 0)) updateOpdPlan(p.id, { price_individual: v }); }} />
                    <label className="text-xs text-zinc-500">Family floater premium (₹)</label>
                    <Input type="number" data-testid={`opd-price-floater-${p.name}`} defaultValue={p.price_floater || 0} className="h-9"
                      onBlur={(e) => { const v = Number(e.target.value); if (v !== (p.price_floater || 0)) updateOpdPlan(p.id, { price_floater: v }); }} />
                    <label className="text-xs text-zinc-500">Cover amount (₹)</label>
                    <Input type="number" data-testid={`opd-cover-${p.name}`} defaultValue={p.cover_amount || 0} className="h-9"
                      onBlur={(e) => { const v = Number(e.target.value); if (v !== (p.cover_amount || 0)) updateOpdPlan(p.id, { cover_amount: v }); }} />
                    <label className="text-xs text-zinc-500">Overall benefits summary <span className="text-zinc-400">(shown to employees in chat)</span></label>
                    <Textarea data-testid={`opd-benefits-summary-${p.name}`} rows={3} defaultValue={p.benefits_summary || ""}
                      placeholder="e.g. Covers doctor consults, diagnostics, pharmacy, dental & teleconsults up to your annual limit."
                      className="text-sm"
                      onBlur={(e) => { const v = e.target.value.trim(); if (v !== (p.benefits_summary || "")) updateOpdPlan(p.id, { benefits_summary: v }); }} />
                  </div>
                  <div className="mt-3 border-t border-zinc-100 pt-2 max-h-40 overflow-y-auto">
                    {(p.benefits || []).map((b, i) => (
                      <p key={i} className="text-[11px] text-zinc-500 py-0.5">{b.benefit}: <b className="text-zinc-700">{b.limit}</b></p>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-2">
              <h2 className="font-heading text-xl font-semibold text-emerald-950">Health Check-up Packages</h2>
              <p className="text-sm text-zinc-500">Prices employees pay when selecting a package in chat.</p>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {checkupPackages.map((p) => (
                <div key={p.id} data-testid={`admin-checkup-${p.name}`} className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center justify-between">
                    <p className="font-heading font-semibold text-emerald-950">{p.name}</p>
                    <label className="flex items-center gap-1.5 text-[11px] text-zinc-500 cursor-pointer" onClick={() => updateCheckupPkg(p.id, { active: !p.active })}>
                      <span className={`relative inline-block h-4 w-8 rounded-full transition-colors ${p.active ? "bg-emerald-600" : "bg-zinc-300"}`}>
                        <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${p.active ? "left-4" : "left-0.5"}`} />
                      </span>{p.active ? "Active" : "Hidden"}
                    </label>
                  </div>
                  <p className="text-xs text-zinc-500 mt-0.5">{p.params} parameters</p>
                  <label className="text-xs text-zinc-500 mt-3 block">Price (₹)</label>
                  <Input type="number" data-testid={`checkup-price-${p.name}`} defaultValue={p.price || 0} className="h-9"
                    onBlur={(e) => { const v = Number(e.target.value); if (v !== (p.price || 0)) updateCheckupPkg(p.id, { price: v }); }} />
                  <div className="mt-3 border-t border-zinc-100 pt-2 max-h-32 overflow-y-auto">
                    {(p.tests || []).map((t, i) => (<p key={i} className="text-[11px] text-zinc-500 py-0.5">• {t}</p>))}
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-2" data-testid="opd-utilization-section">
              <h2 className="font-heading text-xl font-semibold text-emerald-950">OPD Utilization</h2>
              <p className="text-sm text-zinc-500">Log OPD usage against a member's plan. "My OPD" then shows their real remaining balance. Only ₹-amount and session-based benefits are trackable.</p>
            </div>
            {opdEnrollments.length === 0 ? (
              <p className="text-sm text-zinc-400">No active OPD enrollments yet.</p>
            ) : (
              <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
                <div className="grid gap-3 md:grid-cols-5 items-end">
                  <div className="space-y-1 md:col-span-2">
                    <label className="text-xs text-zinc-500">Member (active plan)</label>
                    <select data-testid="util-enrollment" value={utilForm.enrollment_id}
                      onChange={(e) => setUtilForm((f) => ({ ...f, enrollment_id: e.target.value, benefit: "" }))}
                      className="h-9 w-full rounded-md border border-zinc-200 px-2 text-sm">
                      <option value="">Select member…</option>
                      {opdEnrollments.map((en) => (
                        <option key={en.id} value={en.id}>{en.user_name} ({en.member_id}) — {en.plan_name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-zinc-500">Benefit</label>
                    <select data-testid="util-benefit" value={utilForm.benefit} disabled={!utilForm.enrollment_id}
                      onChange={(e) => setUtilForm((f) => ({ ...f, benefit: e.target.value }))}
                      className="h-9 w-full rounded-md border border-zinc-200 px-2 text-sm disabled:opacity-50">
                      <option value="">Select benefit…</option>
                      {(opdEnrollments.find((en) => en.id === utilForm.enrollment_id)?.benefits || [])
                        .filter((b) => isTrackableLimit(b.limit))
                        .map((b, i) => <option key={i} value={b.benefit}>{b.benefit} ({b.limit})</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-zinc-500">{/^\d+\s*session/i.test((opdEnrollments.find((en) => en.id === utilForm.enrollment_id)?.benefits || []).find((b) => b.benefit === utilForm.benefit)?.limit || "") ? "Sessions used" : "Amount used (₹)"}</label>
                    <Input data-testid="util-amount" type="number" value={utilForm.amount}
                      onChange={(e) => setUtilForm((f) => ({ ...f, amount: e.target.value }))} className="h-9" placeholder="e.g. 500" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-zinc-500">Note (optional)</label>
                    <Input data-testid="util-note" value={utilForm.note}
                      onChange={(e) => setUtilForm((f) => ({ ...f, note: e.target.value }))} className="h-9" placeholder="Claim ref / remark" />
                  </div>
                </div>
                <button data-testid="util-submit" onClick={addUtil}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white text-sm font-medium px-4 py-2 transition-colors">
                  Log usage
                </button>
                {opdUtil.length > 0 ? (
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
                        <tr><th className="px-4 py-2">Date</th><th className="px-4 py-2">Member</th><th className="px-4 py-2">Benefit</th><th className="px-4 py-2">Amount</th><th className="px-4 py-2">Note</th><th className="px-4 py-2"></th></tr>
                      </thead>
                      <tbody>
                        {opdUtil.slice(0, 30).map((u) => (
                          <tr key={u.id} data-testid={`util-row-${u.id}`} className="border-t border-zinc-100">
                            <td className="px-4 py-2 text-zinc-500 text-xs">{(u.created_at || "").slice(0, 10)}</td>
                            <td className="px-4 py-2">{u.user_name}<div className="text-[10px] text-zinc-400">{u.member_id}</div></td>
                            <td className="px-4 py-2 text-zinc-600">{u.benefit}</td>
                            <td className="px-4 py-2 font-semibold text-emerald-800">{u.unit === "₹" ? inr(u.amount) : `${u.amount} ${u.unit}(s)`}</td>
                            <td className="px-4 py-2 text-zinc-500 text-xs">{u.note}</td>
                            <td className="px-4 py-2"><button data-testid={`util-delete-${u.id}`} onClick={() => deleteUtil(u.id)} className="text-red-400 hover:text-red-600" title="Remove entry"><Trash size={14} /></button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </div>
            )}

            <div className="pt-2" data-testid="opd-faq-section">
              <h2 className="font-heading text-xl font-semibold text-emerald-950">OPD FAQ (shown to members)</h2>
              <p className="text-sm text-zinc-500">Edit the FAQs shown in the OPD plans section of the chat.</p>
            </div>
            <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm space-y-3">
              {opdFaqs.map((f) => (
                <div key={f.id} data-testid={`admin-faq-${f.id}`} className="flex items-start justify-between gap-3 border-b border-zinc-100 pb-2">
                  <div><p className="text-sm font-medium text-emerald-950">{f.question}</p><p className="text-xs text-zinc-500 mt-0.5">{f.answer}</p></div>
                  <button data-testid={`faq-delete-${f.id}`} onClick={() => deleteOpdFaq(f.id)} className="text-red-400 hover:text-red-600 shrink-0"><Trash size={15} /></button>
                </div>
              ))}
              <div className="grid gap-2 md:grid-cols-2">
                <Input data-testid="faq-question" placeholder="Question" value={faqForm.question} onChange={(e) => setFaqForm((f) => ({ ...f, question: e.target.value }))} className="h-9" />
                <Input data-testid="faq-answer" placeholder="Answer" value={faqForm.answer} onChange={(e) => setFaqForm((f) => ({ ...f, answer: e.target.value }))} className="h-9" />
              </div>
              <button data-testid="faq-add" onClick={addOpdFaq} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white text-sm font-medium px-4 py-2 transition-colors"><Plus size={15} /> Add FAQ</button>
            </div>
          </div>
        )}
        {tab === "payments" && (
          <div className="space-y-4" data-testid="payments-tab">
            <div>
              <h2 className="font-heading text-xl font-semibold text-emerald-950">Payments</h2>
              <p className="text-sm text-zinc-500">{payments.length} payment(s) — OPD plans & health check-up packages (Razorpay).</p>
            </div>
            <div className="rounded-xl border border-zinc-200 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.02)] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
                    <tr>
                      <th className="px-5 py-3">Date</th>
                      <th className="px-5 py-3">Employee</th>
                      <th className="px-5 py-3">Item</th>
                      <th className="px-5 py-3">Amount</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3">Razorpay Payment ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id} data-testid={`payment-row-${p.id}`} className="border-t border-zinc-100">
                        <td className="px-5 py-4 text-zinc-500 text-xs">{(p.created_at || "").slice(0, 10)}</td>
                        <td className="px-5 py-4">{p.user_name}<div className="text-[10px] text-zinc-400">{p.user_email}</div></td>
                        <td className="px-5 py-4 text-zinc-600">{p.item_name}</td>
                        <td className="px-5 py-4 font-semibold text-emerald-800">{inr(p.amount)}</td>
                        <td className="px-5 py-4">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${p.status === "paid" ? "bg-emerald-100 text-emerald-800" : p.status === "failed" ? "bg-red-100 text-red-700" : "bg-zinc-100 text-zinc-500"}`}>{p.status}</span>
                        </td>
                        <td className="px-5 py-4 font-mono text-[10px] text-zinc-500">{p.razorpay_payment_id || "—"}</td>
                      </tr>
                    ))}
                    {payments.length === 0 && (<tr><td colSpan={6} className="px-5 py-10 text-center text-zinc-400">No payments yet.</td></tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "callbacks" && (
          <div className="space-y-4" data-testid="callbacks-tab">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading text-xl font-semibold text-emerald-950">Callback Requests</h2>
                <p className="text-sm text-zinc-500">{callbacks.filter((c) => c.status !== "Done").length} pending · members & website leads who asked the team to call them back.</p>
              </div>
              <button data-testid="export-callbacks" onClick={() => exportXlsx("/admin/callbacks/export", "callbacks_enquiries.xlsx")}
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white text-sm font-medium px-4 py-2 transition-colors">
                <FileXls size={16} /> Export Excel
              </button>
            </div>
            <div className="rounded-xl border border-zinc-200 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.02)] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
                    <tr>
                      <th className="px-5 py-3">Date</th>
                      <th className="px-5 py-3">Member</th>
                      <th className="px-5 py-3">Insurance type</th>
                      <th className="px-5 py-3">Mobile</th>
                      <th className="px-5 py-3">Preferred time</th>
                      <th className="px-5 py-3">Reason</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {callbacks.map((c) => (
                      <tr key={c.id} data-testid={`callback-row-${c.id}`} className="border-t border-zinc-100">
                        <td className="px-5 py-4 text-zinc-500 text-xs">{(c.created_at || "").slice(0, 10)}</td>
                        <td className="px-5 py-4">{c.name}<div className="text-[10px] text-zinc-400">{c.member_id}</div></td>
                        <td className="px-5 py-4">{c.insurance_type ? <span data-testid={`callback-instype-${c.id}`} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-teal-100 text-teal-800">{c.insurance_type}</span> : <span className="text-zinc-300">—</span>}</td>
                        <td className="px-5 py-4 font-mono text-emerald-800">{c.mobile}</td>
                        <td className="px-5 py-4 text-zinc-600">{c.preferred_time || "Any time"}</td>
                        <td className="px-5 py-4 text-zinc-600 max-w-xs">{c.reason || "—"}</td>
                        <td className="px-5 py-4">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${c.status === "Done" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-700"}`}>{c.status}</span>
                        </td>
                        <td className="px-5 py-4">
                          {c.status !== "Done" ? (
                            <button data-testid={`callback-done-${c.id}`} onClick={() => markCallbackDone(c.id)}
                              className="text-xs font-medium text-emerald-700 hover:underline">Mark done</button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                    {callbacks.length === 0 && (<tr><td colSpan={8} className="px-5 py-10 text-center text-zinc-400">No callback requests yet.</td></tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}



        {shotPreview && (
          <div data-testid="slip-preview-overlay" onClick={() => setShotPreview(null)}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6">
            <div className="relative max-h-[90vh] max-w-3xl" onClick={(e) => e.stopPropagation()}>
              <button data-testid="slip-preview-close" onClick={() => setShotPreview(null)}
                className="absolute -right-3 -top-3 h-8 w-8 rounded-full bg-white text-zinc-800 shadow flex items-center justify-center hover:bg-zinc-100">✕</button>
              <img src={shotPreview} alt="Payment slip" className="max-h-[90vh] max-w-full rounded-lg object-contain bg-white" />
            </div>
          </div>
        )}


        {/* POLICY T&C */}
        {tab === "terms" && (
          <div className="space-y-4" data-testid="terms-tab">
            <div>
              <h2 className="font-heading text-xl font-semibold text-emerald-950">Policy Terms & Conditions</h2>
              <p className="text-sm text-zinc-500">Enter T&C per policy number. Members on that policy can read them in chat & WhatsApp (reply "terms").</p>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 shadow-sm space-y-2">
              <p className="text-sm font-semibold text-emerald-950">Add / update terms</p>
              <Input data-testid="term-policy-number" value={termForm.policy_number} placeholder="Policy number e.g. POL-1234567"
                onChange={(e) => setTermForm((f) => ({ ...f, policy_number: e.target.value }))} className="max-w-xs h-9" />
              <Textarea data-testid="term-text" rows={5} value={termForm.terms} placeholder="Enter the terms & conditions…"
                onChange={(e) => setTermForm((f) => ({ ...f, terms: e.target.value }))} />
              <Button data-testid="term-save" onClick={() => saveTerms(termForm.policy_number, termForm.terms)}
                className="rounded-full bg-emerald-900 hover:bg-emerald-800 text-white">Save terms</Button>
            </div>
            <div className="space-y-3">
              {policyTerms.map((t) => (
                <div key={t.policy_number} data-testid={`term-row-${t.policy_number}`} className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <p className="font-mono text-sm font-semibold text-emerald-800">{t.policy_number}</p>
                    <button data-testid={`term-delete-${t.policy_number}`} onClick={() => deleteTerms(t.policy_number)} className="text-red-400 hover:text-red-600"><Trash size={15} /></button>
                  </div>
                  <Textarea rows={4} defaultValue={t.terms} className="mt-2 text-sm"
                    onBlur={(e) => { if (e.target.value !== t.terms) saveTerms(t.policy_number, e.target.value); }} />
                </div>
              ))}
              {policyTerms.length === 0 && <p className="text-sm text-zinc-400">No policy terms added yet.</p>}
            </div>
          </div>
        )}

        {/* EMPLOYEES */}
        {tab === "employees" && (
          <div className="space-y-4">
            <Dialog open={onboardOpen} onOpenChange={setOnboardOpen}>
              <DialogContent data-testid="onboarding-dialog">
                <DialogHeader><DialogTitle>Send onboarding to employees</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-zinc-600">Emails/WhatsApp with each member's <b>e-card link</b> and a note that their <b>password is their date of birth</b>.</p>
                  {onboardPreview && (
                    <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-3 text-sm text-zinc-700">
                      <p>Total employees: <b>{onboardPreview.total}</b> · Not yet onboarded: <b>{onboardPreview.not_yet_onboarded}</b></p>
                      <p className="mt-1 text-xs text-zinc-500">E-card link: <span className="font-mono">{onboardPreview.ecard_url}</span></p>
                      {!onboardPreview.channels_ready.email && <p className="mt-1 text-xs text-amber-600">Email is in DEMO mode — verify your Resend domain to actually deliver.</p>}
                      {!onboardPreview.channels_ready.whatsapp && <p className="mt-1 text-xs text-amber-600">WhatsApp sending is not configured (Twilio).</p>}
                    </div>
                  )}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="onboard-email" checked={onboardCh.email} onChange={(e) => setOnboardCh((c) => ({ ...c, email: e.target.checked }))} /> Send email</label>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="onboard-whatsapp" checked={onboardCh.whatsapp} onChange={(e) => setOnboardCh((c) => ({ ...c, whatsapp: e.target.checked }))} /> Send WhatsApp <span className="text-xs text-zinc-400">(delivers within the 24h session or via approved template)</span></label>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="onboard-only-new" checked={onboardOnlyNew} onChange={(e) => setOnboardOnlyNew(e.target.checked)} /> Only those not onboarded yet</label>
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="onboard-send" onClick={runOnboard} disabled={onboardBusy}
                    className="rounded-full bg-emerald-900 hover:bg-emerald-800 text-white">
                    {onboardBusy ? "Sending…" : "Send onboarding"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <form onSubmit={doLookup} data-testid="member-lookup-box"
              className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 shadow-sm">
              <p className="font-heading font-semibold text-emerald-950 text-sm">Look up a member</p>
              <p className="text-xs text-zinc-500 mb-2">Search by employee code, phone number, member ID or UHID to see their policy & family.</p>
              <div className="flex flex-wrap gap-2">
                <Input data-testid="member-lookup-input" value={lookupQuery} onChange={(e) => setLookupQuery(e.target.value)}
                  placeholder="e.g. EMP1001 or 9876543210" className="h-9 max-w-xs" />
                <Button type="submit" data-testid="member-lookup-btn" disabled={lookupBusy}
                  className="h-9 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white">
                  {lookupBusy ? "Searching…" : "Look up"}
                </Button>
                {lookupResult && (
                  <Button type="button" variant="ghost" onClick={() => { setLookupResult(null); setLookupQuery(""); }}
                    className="h-9 text-zinc-500">Clear</Button>
                )}
              </div>
              {lookupResult && (
                <div data-testid="member-lookup-result" className="mt-3 rounded-lg border border-emerald-200 bg-white p-4">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="font-heading font-semibold text-emerald-950">{lookupResult.name}</p>
                    <span className="text-xs text-zinc-600 font-medium">{lookupResult.company}</span>
                  </div>
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs text-zinc-600">
                    {lookupResult.employee_code ? <p>Employee code: <b className="text-zinc-800">{lookupResult.employee_code}</b></p> : null}
                    <p>Member ID: <b className="text-zinc-800">{lookupResult.member_id}</b></p>
                    {lookupResult.uhid ? <p>UHID: <b className="text-zinc-800">{lookupResult.uhid}</b></p> : null}
                    <p>Mobile: <b className="text-zinc-800">{lookupResult.mobile || "—"}</b></p>
                    <p>Plan: <b className="text-zinc-800">{lookupResult.plan_name}</b></p>
                    <p>Sum insured: <b className="text-zinc-800">{inr(lookupResult.sum_insured)}</b></p>
                  </div>
                  {lookupResult.dependents?.length ? (
                    <div className="mt-2 border-t border-zinc-100 pt-2">
                      <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wide">Family covered</p>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {lookupResult.dependents.map((d, i) => (
                          <span key={i} className="text-xs bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5 text-emerald-800">
                            {d.name} · {d.relation}{d.age ? ` · ${d.age}y` : ""}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : <p className="mt-2 text-xs text-zinc-400">No family members mapped.</p>}
                </div>
              )}
            </form>
            <div className="flex items-center gap-2">
              <label className="text-sm text-zinc-500">Filter by company:</label>
              <select data-testid="company-filter" value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)}
                className="h-9 rounded-md border border-zinc-200 px-2 text-sm max-w-xs">
                <option value="all">All companies</option>
                {[...new Set(employees.map((e) => e.company).filter(Boolean))].sort().map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="rounded-xl border border-zinc-200 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.02)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="employees-table">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                    {isMaster && (
                      <th className="px-5 py-3 w-10">
                        <input type="checkbox" data-testid="select-all-emps" className="h-4 w-4 accent-emerald-700 cursor-pointer"
                          checked={(() => { const f = employees.filter((e) => companyFilter === "all" || e.company === companyFilter); return f.length > 0 && f.every((e) => selectedEmps.has(e.id)); })()}
                          onChange={(ev) => toggleSelectAll(employees.filter((e) => companyFilter === "all" || e.company === companyFilter).map((e) => e.id), ev.target.checked)} />
                      </th>
                    )}
                    <th className="px-5 py-3">Member</th>
                    <th className="px-5 py-3">Emp code</th>
                    <th className="px-5 py-3">Company</th>
                    <th className="px-5 py-3">Member ID / UHID</th>
                    <th className="px-5 py-3">Mobile</th>
                    <th className="px-5 py-3">Plan</th>
                    <th className="px-5 py-3 text-right">Sum insured</th>
                    <th className="px-5 py-3 text-right">OPD cover</th>
                    <th className="px-5 py-3 text-right">Family</th>
                    <th className="px-5 py-3 text-center">Onboarding</th>
                    <th className="px-5 py-3 text-right">Claims</th>
                    <th className="px-5 py-3 text-right">Manage</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.filter((e) => companyFilter === "all" || e.company === companyFilter).map((e) => (
                    <tr key={e.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                      {isMaster && (
                        <td className="px-5 py-4">
                          <input type="checkbox" data-testid={`emp-select-${e.id}`} className="h-4 w-4 accent-emerald-700 cursor-pointer"
                            checked={selectedEmps.has(e.id)} onChange={() => toggleSelectEmp(e.id)} />
                        </td>
                      )}
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center text-sm font-bold">
                            {e.name?.[0]}
                          </div>
                          <div>
                            <p className="font-medium text-zinc-800">{e.name}</p>
                            <p className="text-xs text-zinc-400">{e.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 font-mono text-xs text-zinc-600">{e.employee_code || "—"}</td>
                      <td className="px-5 py-4 text-xs text-emerald-700" data-testid={`emp-company-${e.id}`}>{e.company || "—"}</td>
                      <td className="px-5 py-4 font-mono text-xs text-zinc-600">{e.member_id}{e.uhid ? <div className="text-[10px] text-zinc-400">UHID {e.uhid}</div> : null}</td>
                      <td className="px-5 py-4 font-mono text-xs text-zinc-600">{e.mobile || "—"}</td>
                      <td className="px-5 py-4">{e.policy?.plan_name}</td>
                      <td className="px-5 py-4 text-right font-mono text-emerald-800">{inr(e.policy?.sum_insured)}</td>
                      <td className="px-5 py-4 text-right font-mono text-emerald-700" data-testid={`emp-opd-cover-${e.id}`}>{e.policy?.opd_cover != null ? inr(e.policy.opd_cover) : "—"}</td>
                      <td className="px-5 py-4 text-right text-zinc-600">{(e.policy?.dependents || []).length || "—"}</td>
                      <td className="px-5 py-4 text-center">
                        {e.onboarded_at ? (
                          <span data-testid={`onboard-status-${e.id}`} title={`Sent ${new Date(e.onboarded_at).toLocaleDateString()}`}
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium px-2.5 py-1">
                            <Check size={12} weight="bold" /> Sent
                          </span>
                        ) : (
                          <span data-testid={`onboard-status-${e.id}`}
                            className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium px-2.5 py-1">
                            Not sent
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-right">{e.claim_count}</td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            data-testid={`manage-family-${e.id}`}
                            onClick={() => setDepEmp(e)}
                            className="inline-flex items-center gap-1 rounded-full border border-emerald-200 text-emerald-800 text-xs font-medium px-3 py-1.5 hover:bg-emerald-50 transition-colors"
                          >
                            <UsersThree size={14} /> Family ({e.policy?.dependents?.length || 0})
                          </button>
                          <label className="cursor-pointer inline-flex items-center gap-1 rounded-full border border-zinc-200 text-zinc-600 text-xs font-medium px-3 py-1.5 hover:bg-zinc-50 transition-colors" data-testid={`upload-emp-doc-${e.id}`}>
                            <UploadSimple size={14} /> Doc
                            <input type="file" className="hidden" accept=".pdf,.doc,.docx,.png,.jpg" onChange={(ev) => uploadDocument(ev, "employee", e.id)} />
                          </label>
                          <button
                            data-testid={`send-welcome-${e.id}`}
                            onClick={() => sendWelcome(e)}
                            title="Email this employee their member ID and how to chat on WhatsApp"
                            className="inline-flex items-center gap-1 rounded-full border border-emerald-200 text-emerald-800 text-xs font-medium px-3 py-1.5 hover:bg-emerald-50 transition-colors"
                          >
                            <EnvelopeSimple size={14} /> Welcome
                          </button>
                          {isMaster && (
                            <>
                              <button
                                data-testid={`promote-${e.id}`}
                                onClick={() => changeRole(e.id, "admin")}
                                title="Promote this employee to HR Admin"
                                className="inline-flex items-center gap-1 rounded-full border border-amber-300 text-amber-800 text-xs font-medium px-3 py-1.5 hover:bg-amber-50 transition-colors"
                              >
                                <ShieldStar size={14} /> Make HR Admin
                              </button>
                              <button
                                data-testid={`delete-user-${e.id}`}
                                onClick={() => deleteUser(e.id, e.name)}
                                title="Delete this user"
                                className="inline-flex items-center gap-1 rounded-full border border-red-200 text-red-600 text-xs font-medium px-3 py-1.5 hover:bg-red-50 transition-colors"
                              >
                                <Trash size={14} /> Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {employees.length === 0 && (
                    <tr><td colSpan={isMaster ? 13 : 12} className="px-5 py-10 text-center text-zinc-400">No employees yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          </div>
        )}

        {/* DOCUMENTS */}
        {tab === "documents" && (
          <div className="rounded-xl border border-zinc-200 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.02)] overflow-hidden" data-testid="documents-panel">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                    <th className="px-5 py-3">Document</th>
                    <th className="px-5 py-3">Scope</th>
                    <th className="px-5 py-3">Assigned to</th>
                    <th className="px-5 py-3">Uploaded</th>
                    <th className="px-5 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((d) => (
                    <tr key={d.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                      <td className="px-5 py-4 flex items-center gap-2 text-zinc-800"><Files size={16} className="text-emerald-600" /> {d.filename}</td>
                      <td className="px-5 py-4">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${d.scope === "company" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>
                          {d.scope === "company" ? "Company-wide" : "Employee"}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-zinc-600">{d.employee_name || "All employees"}</td>
                      <td className="px-5 py-4 text-xs text-zinc-400">{new Date(d.uploaded_at).toLocaleDateString()}</td>
                      <td className="px-5 py-4 text-right">
                        <button data-testid={`delete-doc-${d.id}`} onClick={() => deleteDocument(d.id)} className="text-red-500 hover:text-red-700 transition-colors">
                          <Trash size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {documents.length === 0 && (
                    <tr><td colSpan={5} className="px-5 py-10 text-center text-zinc-400">No documents uploaded yet. Use "Upload company policy" above, or the Doc button on an employee row.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* NETWORK HOSPITALS */}
        {tab === "network" && (
          <div className="space-y-4" data-testid="network-panel">
            <div className="rounded-xl border border-dashed border-emerald-300 bg-emerald-50/60 p-4 text-sm text-emerald-800">
              Import a CSV with columns: <span className="font-mono text-xs">name, city, area, address, specialties, cashless, phone, insurer, policy_number</span>. Separate multiple specialties with <span className="font-mono">;</span>. Map a hospital to a specific <b>insurer</b> and <b>policy number</b> so only members on that policy see it.
            </div>
            <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm" data-testid="add-hospital-form">
              <p className="font-heading font-semibold text-emerald-950 text-sm mb-3">Add a network hospital</p>
              <div className="grid gap-3 md:grid-cols-3">
                <Input data-testid="hosp-name" placeholder="Hospital name *" value={newHosp.name} onChange={(e) => setNewHosp((h) => ({ ...h, name: e.target.value }))} className="h-9" />
                <Input data-testid="hosp-city" placeholder="City" value={newHosp.city} onChange={(e) => setNewHosp((h) => ({ ...h, city: e.target.value }))} className="h-9" />
                <Input data-testid="hosp-area" placeholder="Area" value={newHosp.area} onChange={(e) => setNewHosp((h) => ({ ...h, area: e.target.value }))} className="h-9" />
                <Input data-testid="hosp-specialties" placeholder="Specialties (semicolon-separated)" value={newHosp.specialties} onChange={(e) => setNewHosp((h) => ({ ...h, specialties: e.target.value }))} className="h-9 md:col-span-2" />
                <Input data-testid="hosp-phone" placeholder="Phone" value={newHosp.phone} onChange={(e) => setNewHosp((h) => ({ ...h, phone: e.target.value }))} className="h-9" />
                <select data-testid="hosp-insurer" value={newHosp.insurer} onChange={(e) => setNewHosp((h) => ({ ...h, insurer: e.target.value }))} className="h-9 rounded-md border border-zinc-200 px-2 text-sm">
                  <option value="">Insurer / TPA (optional)</option>
                  {insurers.map((ins) => <option key={ins.id} value={ins.name}>{ins.name}</option>)}
                </select>
                <Input data-testid="hosp-policy" placeholder="Policy number (optional)" value={newHosp.policy_number} onChange={(e) => setNewHosp((h) => ({ ...h, policy_number: e.target.value }))} className="h-9" />
                <label className="flex items-center gap-2 text-xs text-zinc-500">
                  <input type="checkbox" data-testid="hosp-cashless" checked={newHosp.cashless} onChange={(e) => setNewHosp((h) => ({ ...h, cashless: e.target.checked }))} /> Cashless available
                </label>
              </div>
              <button data-testid="hosp-add-submit" onClick={addHospital}
                className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white text-sm font-medium px-4 py-2 transition-colors">
                <Plus size={15} /> Add hospital
              </button>
            </div>
            <div className="rounded-xl border border-zinc-200 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.02)] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="hospitals-table">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                      <th className="px-5 py-3">Hospital</th>
                      <th className="px-5 py-3">City / Area</th>
                      <th className="px-5 py-3">Specialties</th>
                      <th className="px-5 py-3">Insurer / TPA</th>
                      <th className="px-5 py-3">Policy</th>
                      <th className="px-5 py-3">Cashless</th>
                      <th className="px-5 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hospitals.map((h) => (
                      <tr key={h.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                        <td className="px-5 py-4 font-medium text-zinc-800">{h.name}</td>
                        <td className="px-5 py-4 text-zinc-600">{h.city}{h.area ? `, ${h.area}` : ""}</td>
                        <td className="px-5 py-4">
                          <div className="flex flex-wrap gap-1">
                            {(h.specialties || []).slice(0, 3).map((s, i) => (
                              <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600">{s}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-emerald-700 text-xs font-medium">{h.insurer || "All insurers"}</td>
                        <td className="px-5 py-4 text-xs font-mono text-zinc-600">{h.policy_number || "All policies"}</td>
                        <td className="px-5 py-4">
                          {h.cashless
                            ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Yes</span>
                            : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600">No</span>}
                        </td>
                        <td className="px-5 py-4 text-right">
                          <button data-testid={`delete-hospital-${h.id}`} onClick={() => deleteHospital(h.id)} className="text-red-500 hover:text-red-700 transition-colors">
                            <Trash size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {hospitals.length === 0 && (
                      <tr><td colSpan={7} className="px-5 py-10 text-center text-zinc-400">No hospitals yet. Add one above or import a CSV to get started.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* INSURERS & TPA */}
        {tab === "insurers" && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="insurers-panel">
            {insurers.map((ins) => (
              <div key={ins.id} className="rounded-xl border border-zinc-200 bg-white p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
                <div className="flex items-start justify-between">
                  <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <Bank size={22} weight="duotone" className="text-emerald-700" />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold uppercase tracking-widest ${ins.type === "TPA" ? "text-amber-600" : "text-emerald-600"}`}>{ins.type}</span>
                    <button data-testid={`delete-insurer-${ins.id}`} onClick={() => deleteInsurer(ins.id)} className="text-red-400 hover:text-red-600 transition-colors">
                      <Trash size={15} />
                    </button>
                  </div>
                </div>
                <p className="mt-3 font-heading font-semibold text-emerald-950">{ins.name}</p>
                {ins.contact && <p className="text-sm text-zinc-500 mt-1">{ins.contact}</p>}
                {ins.website && <p className="text-xs text-emerald-600 mt-0.5">{ins.website}</p>}
              </div>
            ))}
            {insurers.length === 0 && (
              <p className="text-zinc-400 text-sm">No insurers or TPAs yet. Add one from the button above.</p>
            )}
          </div>
        )}

        {/* WHATSAPP */}
        {tab === "whatsapp" && (
          <div className="grid lg:grid-cols-2 gap-6 max-w-4xl" data-testid="whatsapp-panel">
            <div className="rounded-xl border border-zinc-200 bg-white p-6 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <WhatsappLogo size={24} weight="fill" className="text-emerald-600" />
                </div>
                <div>
                  <p className="font-heading font-semibold text-emerald-950">Connect WhatsApp</p>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${whatsapp.connected ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                    {whatsapp.status || "Not connected"}
                  </span>
                </div>
              </div>
              <p className="text-sm text-zinc-500 mb-5">
                {whatsapp.connected
                  ? "WhatsApp is live. Employees can message your business number and the AI assistant replies automatically."
                  : "Add your Twilio credentials to backend/.env (TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_WHATSAPP_FROM) and restart, then paste the webhook URL below into your Twilio WhatsApp sender's inbound settings."}
              </p>

              <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-3 mb-5">
                <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Inbound webhook URL</p>
                <div className="flex items-center gap-2">
                  <code data-testid="wa-webhook-url" className="flex-1 text-xs font-mono text-emerald-800 break-all">{webhookUrl}</code>
                  <button type="button" data-testid="wa-copy-webhook"
                    onClick={() => { navigator.clipboard?.writeText(webhookUrl); toast.success("Webhook URL copied"); }}
                    className="shrink-0 rounded-md border border-zinc-200 px-2 py-1 text-xs text-emerald-800 hover:bg-emerald-50 transition-colors">Copy</button>
                </div>
                <p className="text-[11px] text-zinc-400 mt-1.5">Set this as the "When a message comes in" (HTTP POST) URL in Twilio.</p>
              </div>

              <form onSubmit={saveWhatsapp} className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Provider</Label>
                  <Select value={whatsapp.provider} onValueChange={(v) => setWhatsapp({ ...whatsapp, provider: v })}>
                    <SelectTrigger data-testid="wa-provider"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Twilio">Twilio</SelectItem>
                      <SelectItem value="Meta Cloud API">Meta Cloud API</SelectItem>
                      <SelectItem value="Gupshup">Gupshup</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>WhatsApp business number</Label>
                  <Input data-testid="wa-number" value={whatsapp.number}
                    onChange={(e) => setWhatsapp({ ...whatsapp, number: e.target.value })} placeholder="+91 90000 00000" />
                </div>
                <Button type="submit" data-testid="wa-save" className="rounded-full bg-emerald-900 hover:bg-emerald-800 text-white transition-colors">
                  Save details
                </Button>
              </form>
            </div>

            <div className="rounded-xl border border-zinc-200 bg-emerald-950 p-6 text-white flex flex-col items-center justify-center text-center grain">
              <div className={`px-3 py-1 rounded-full text-xs font-semibold mb-4 ${whatsapp.connected ? "bg-emerald-500 text-emerald-950" : "bg-amber-400/90 text-amber-950"}`}>
                {whatsapp.connected ? "● Live" : "○ Not connected"}
              </div>
              <div className="bg-white p-4 rounded-2xl">
                <QrCode size={140} weight="duotone" className="text-emerald-950" />
              </div>
              <p className="mt-5 font-heading font-semibold">{whatsapp.connected ? "Message us on WhatsApp" : "Scan to chat (once live)"}</p>
              <p className="text-sm text-emerald-200/70 mt-1 max-w-xs">
                {whatsapp.connected
                  ? `Employees can message ${whatsapp.number} on WhatsApp anytime — the AI replies instantly with their policy details.`
                  : "Once connected, employees message your number to chat with the assistant on WhatsApp."}
              </p>
            </div>

            {isMaster && (
              <div className="lg:col-span-2 rounded-xl border border-zinc-200 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.02)] overflow-hidden" data-testid="wa-logs-panel">
                <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
                  <p className="font-heading font-semibold text-emerald-950">Message delivery log</p>
                  <button onClick={loadWaLogs} className="text-xs text-emerald-700 hover:underline">Refresh</button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                        <th className="px-5 py-3">To</th>
                        <th className="px-5 py-3">Message</th>
                        <th className="px-5 py-3">Status</th>
                        <th className="px-5 py-3">When</th>
                      </tr>
                    </thead>
                    <tbody>
                      {waLogs.map((m) => {
                        const tone = /deliver|read|sent/.test(m.status) ? "bg-emerald-100 text-emerald-800"
                          : /fail|undeliver/.test(m.status) ? "bg-red-100 text-red-800"
                          : "bg-amber-100 text-amber-800";
                        return (
                          <tr key={m.id} className="border-b border-zinc-100">
                            <td className="px-5 py-3 font-mono text-xs text-zinc-600">{m.to}</td>
                            <td className="px-5 py-3 text-zinc-700 max-w-md truncate">{m.body}</td>
                            <td className="px-5 py-3"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${tone}`}>{m.status}</span></td>
                            <td className="px-5 py-3 text-xs text-zinc-400">{new Date(m.created_at).toLocaleString()}</td>
                          </tr>
                        );
                      })}
                      {waLogs.length === 0 && (
                        <tr><td colSpan={4} className="px-5 py-8 text-center text-zinc-400">No WhatsApp messages sent yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* FAQ MANAGER */}
        {tab === "faqs" && (
          <div className="rounded-xl border border-zinc-200 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.02)] overflow-hidden" data-testid="faqs-panel">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                    <th className="px-5 py-3">Product</th>
                    <th className="px-5 py-3">Category</th>
                    <th className="px-5 py-3">Question &amp; Answer</th>
                    <th className="px-5 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {faqs.map((f) => (
                    <tr key={f.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors align-top">
                      <td className="px-5 py-4">
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">{f.product || "All Products"}</span>
                      </td>
                      <td className="px-5 py-4 text-zinc-600">{f.category}</td>
                      <td className="px-5 py-4 max-w-md">
                        <p className="font-medium text-zinc-800">{f.question}</p>
                        <p className="text-xs text-zinc-500 mt-0.5">{f.answer}</p>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <button data-testid={`delete-faq-${f.id}`} onClick={() => deleteFaq(f.id)} className="text-red-500 hover:text-red-700 transition-colors"><Trash size={16} /></button>
                      </td>
                    </tr>
                  ))}
                  {faqs.length === 0 && (
                    <tr><td colSpan={4} className="px-5 py-10 text-center text-zinc-400">No FAQs yet. Add one above.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ADMINS (master only) */}
        {tab === "admins" && (
          <div className="space-y-6" data-testid="admins-panel">
            <div className="rounded-xl border border-zinc-200 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-5" data-testid="digest-card">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-heading font-semibold text-emerald-950">Daily Digest</p>
                  <p className="text-xs text-zinc-500">Morning summary of new claims &amp; pending approvals, sent to master admins.</p>
                </div>
                <Button onClick={sendDigest} data-testid="send-digest-btn" className="rounded-full bg-emerald-900 hover:bg-emerald-800 text-white transition-colors">
                  Send now
                </Button>
              </div>
              <pre className="rounded-lg bg-emerald-950 text-emerald-100 text-xs p-4 whitespace-pre-wrap font-mono overflow-x-auto" data-testid="digest-preview">{digest?.text || "Loading…"}</pre>
              {digestCfg && (
                <div className="mt-4 flex flex-wrap items-center gap-4 rounded-lg bg-zinc-50 border border-zinc-200 p-3" data-testid="digest-schedule">
                  <label className="flex items-center gap-2 text-sm text-zinc-700">
                    <input type="checkbox" data-testid="digest-enabled" checked={digestCfg.enabled}
                      onChange={(e) => setDigestCfg({ ...digestCfg, enabled: e.target.checked })} className="accent-emerald-700" />
                    Auto-send daily
                  </label>
                  <label className="flex items-center gap-2 text-sm text-zinc-700">
                    Time (UTC)
                    <input type="number" min="0" max="23" data-testid="digest-hour" value={digestCfg.hour_utc}
                      onChange={(e) => setDigestCfg({ ...digestCfg, hour_utc: Number(e.target.value) })}
                      className="w-16 rounded-md border border-zinc-200 px-2 py-1 text-sm" />
                    :
                    <input type="number" min="0" max="59" data-testid="digest-minute" value={digestCfg.minute}
                      onChange={(e) => setDigestCfg({ ...digestCfg, minute: Number(e.target.value) })}
                      className="w-16 rounded-md border border-zinc-200 px-2 py-1 text-sm" />
                  </label>
                  <label className="flex items-center gap-2 text-sm text-zinc-700">
                    <input type="checkbox" data-testid="digest-ch-email" checked={digestCfg.email}
                      onChange={(e) => setDigestCfg({ ...digestCfg, email: e.target.checked })} className="accent-emerald-700" />
                    Email
                  </label>
                  <label className="flex items-center gap-2 text-sm text-zinc-700">
                    <input type="checkbox" data-testid="digest-ch-whatsapp" checked={digestCfg.whatsapp}
                      onChange={(e) => setDigestCfg({ ...digestCfg, whatsapp: e.target.checked })} className="accent-emerald-700" />
                    WhatsApp
                  </label>
                  <button onClick={saveDigestCfg} data-testid="digest-save-schedule"
                    className="ml-auto rounded-full border border-emerald-300 text-emerald-800 text-sm font-medium px-4 py-1.5 hover:bg-emerald-50 transition-colors">
                    Save schedule
                  </button>
                </div>
              )}
              <p className="text-[11px] text-zinc-400 mt-2">
                Delivery channels ready: Email {digest?.channels_ready?.email ? "✅" : "○ (add Resend key)"} · WhatsApp {digest?.channels_ready?.whatsapp ? "✅" : "○ (add Twilio keys)"}
              </p>
            </div>

            <div className="rounded-xl border border-zinc-200 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.02)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                    <th className="px-5 py-3">Name</th>
                    <th className="px-5 py-3">Email</th>
                    <th className="px-5 py-3">Mobile</th>
                    <th className="px-5 py-3">Role</th>
                    <th className="px-5 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map((a) => (
                    <tr key={a.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                      <td className="px-5 py-4 font-medium text-zinc-800">{a.name}</td>
                      <td className="px-5 py-4 text-zinc-600">{a.email}</td>
                      <td className="px-5 py-4 text-zinc-600 font-mono text-xs">{a.mobile || "—"}</td>
                      <td className="px-5 py-4">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${a.role === "master_admin" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                          {a.role === "master_admin" ? "Master Admin" : "HR Admin"}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        {a.id !== user?.id ? (
                          <div className="flex items-center justify-end gap-2">
                            <button data-testid={`demote-admin-${a.id}`} onClick={() => changeRole(a.id, "employee")}
                              title="Demote to Employee"
                              className="inline-flex items-center gap-1 rounded-full border border-zinc-200 text-zinc-600 text-xs font-medium px-3 py-1.5 hover:bg-zinc-50 transition-colors">
                              Demote
                            </button>
                            <button data-testid={`delete-admin-${a.id}`} onClick={() => deleteUser(a.id, a.name)} className="text-red-500 hover:text-red-700 transition-colors"><Trash size={16} /></button>
                          </div>
                        ) : (
                          <span className="text-[11px] text-zinc-400">You</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          </div>
        )}

        {/* DEPENDENTS DIALOG */}
        <Dialog open={!!depEmp} onOpenChange={(o) => !o && setDepEmp(null)}>
          <DialogContent data-testid="dependents-dialog">
            <DialogHeader>
              <DialogTitle>Family members — {depEmp?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {(depEmp?.policy?.dependents || []).map((d) => (
                <div key={d.id} className="flex items-center justify-between rounded-lg bg-zinc-50 p-3">
                  <div>
                    <p className="text-sm font-medium text-zinc-800">{d.name}</p>
                    <p className="text-xs text-zinc-500">{d.relation} · {d.age} yrs</p>
                  </div>
                  <button data-testid={`remove-dep-${d.id}`} onClick={() => removeDependent(d.id)} className="text-red-500 hover:text-red-700 transition-colors">
                    <Trash size={15} />
                  </button>
                </div>
              ))}
              {(!depEmp?.policy?.dependents || depEmp.policy.dependents.length === 0) && (
                <p className="text-sm text-zinc-400 text-center py-4">No dependents yet.</p>
              )}
            </div>
            <form onSubmit={addDependent} className="border-t border-zinc-100 pt-4 space-y-3">
              <p className="text-xs font-bold uppercase tracking-wider text-emerald-600">Add a dependent</p>
              <div className="grid grid-cols-2 gap-3">
                <Input required placeholder="Name" data-testid="dep-name" value={newDep.name}
                  onChange={(e) => setNewDep({ ...newDep, name: e.target.value })} />
                <Select value={newDep.relation} onValueChange={(v) => setNewDep({ ...newDep, relation: v })}>
                  <SelectTrigger data-testid="dep-relation"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Spouse">Spouse</SelectItem>
                    <SelectItem value="Son">Son</SelectItem>
                    <SelectItem value="Daughter">Daughter</SelectItem>
                    <SelectItem value="Father">Father</SelectItem>
                    <SelectItem value="Mother">Mother</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Input required type="number" placeholder="Age" data-testid="dep-age" value={newDep.age}
                onChange={(e) => setNewDep({ ...newDep, age: e.target.value })} />
              <Button type="submit" data-testid="dep-submit" className="w-full rounded-full bg-emerald-900 hover:bg-emerald-800 text-white transition-colors">
                <Plus size={16} className="mr-1" /> Add dependent
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
